<template>
  <div class="gallery-tab">
    <div class="gallery-container">
      <!-- Gallery Header -->
      <div class="gallery-header">  
        <div class="gallery-icon">
          <n-icon size="24">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21,15 16,10 5,21"/>
            </svg>
          </n-icon>
        </div>
        <div class="gallery-info">
          <h2 class="gallery-title">Image Gallery</h2>
          <p class="gallery-subtitle">Manage and organize your image collection</p>
        </div>
        <div class="header-actions">
          <template v-if="currentGalleryType === 'type-library'">
            <button class="refresh-button" @click="loadFonts" title="Refresh type library">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
              </svg>
              <span>Refresh</span>
            </button>
          </template>

          <!-- Image gallery upload button -->
          <button
            v-else
            class="upload-button"
            :disabled="!currentGalleryId || isIngesting"
            @click="toggleUploadPane"
            :class="{ 'active': showUploadPane }"
            title="Upload images or ingest a folder"
          >
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
            <span>{{ isIngesting ? 'Uploading...' : 'Upload' }}</span>
          </button>
          <button v-if="currentGalleryType !== 'type-library'" class="refresh-button" @click="refreshGallery" title="Refresh gallery">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
            </svg>
            <span>Refresh</span>
          </button>
          <button
            v-if="currentGalleryType !== 'type-library'"
            class="upload-button export-button"
            :disabled="!currentGalleryId || isExporting"
            @click="handleExportGallery"
            title="Export gallery (images + metadata as ZIP)"
          >
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M13.5,16V19H10.5V16H8L12,12L16,16H13.5M13,9V3.5L18.5,9H13Z" />
            </svg>
            <span>{{ isExporting ? 'Exporting...' : 'Export' }}</span>
          </button>
          <button
            v-if="currentGalleryType !== 'type-library'"
            class="upload-button import-button"
            :disabled="!currentGalleryId || isImporting"
            @click="handleImportGallery"
            title="Import into selected gallery (folder or files; with metadata.json creates gallery)"
          >
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M13.5,17V14H10.5V17H8L12,21L16,17H13.5M13,9V3.5L18.5,9H13Z" />
            </svg>
            <span>{{ isImporting ? 'Importing...' : 'Import' }}</span>
          </button>
          <input
            ref="importFileInputRef"
            type="file"
            multiple
            accept=".json,image/*"
            style="display: none"
            @change="handleImportFileInput"
          />
          <button 
            v-if="hasDeletedImages"
            class="restore-trash-button" 
            @click="restoreTrash" 
            title="Restore all deleted images"
          >
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M14,14H22L20,12H18V8A2,2 0 0,0 16,6H8V4H6V6H4V8H6V16A2,2 0 0,0 8,18H14M20,20H4V18H20V20Z" />
            </svg>
            <span>Restore Trash</span>
          </button>
          <button 
            v-if="hasDeletedImages"
            class="empty-trash-button" 
            @click="emptyTrash" 
            title="Empty trash - permanently delete all marked images"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" style="color: #ff4d4f;">
              <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
            </svg>
            <span>Empty Trash</span>
          </button>
          <button class="clear-db-button" @click="confirmClearDatabase" title="Clear all images (testing)">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
            </svg>
            <span>Clear DB</span>
          </button>
        </div>
      </div>

      <!-- Filters and Controls -->
      <div class="gallery-controls">
        <div class="control-group gallery-selector-group">
          <GallerySelector
            v-model="currentGalleryId"
            :brand-id="selectedBrandId"
            @change="handleGalleryChange"
            class="gallery-selector-component"
          />
          <span class="results-count">
            <template v-if="filterTags.length">{{ visibleImages.length }} of {{ totalImages }} image{{ totalImages !== 1 ? 's' : '' }}</template>
            <template v-else>{{ totalImages }} image{{ totalImages !== 1 ? 's' : '' }}</template>
          </span>
          <div v-if="currentGalleryType === 'type-library'" class="control-group">
            <input
              v-model="fontSearch"
              type="text"
              class="search-input"
              placeholder="Name..."
              @input="loadFonts"
            />
          </div>
          <div class="control-group">
            <input
              v-model="filterInput"
              type="text"
              class="search-input"
              :placeholder="currentGalleryType === 'type-library' ? 'Tags...' : 'Filter images...'"
              @keyup.enter="addFilterTag"
            />
            <button v-if="currentGalleryType !== 'type-library'" class="search-button" @click="addFilterTag">Filter</button>
          </div>
        </div>

        <div class="controls-right">
          <div class="control-group moodboard-selector-group">
            <label class="moodboard-selector-label">Add to moodboard</label>
            <n-select
              v-model:value="currentMoodboardIdForBookmark"
              :options="moodboardSelectOptions"
              placeholder="Select moodboard"
              clearable
              class="moodboard-select"
            />
          </div>

          <div class="control-group">
            <select v-model="sortBy" @change="handleSortByChange" class="sort-select">
              <option value="createdAt">Sort by Date</option>
              <option value="filename">Sort by Name</option>
              <option value="colorSimilarity">Sort by Color Similarity</option>
            </select>
            <button
              class="sort-order-button"
              @click="sortOrder = sortOrder === 'desc' ? 'asc' : 'desc'; refreshGallery()"
              :title="`Sort ${sortOrder === 'desc' ? 'ascending' : 'descending'}`"
            >
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path v-if="sortOrder === 'desc'" fill="currentColor" d="M7,10L12,15L17,10H7Z" />
                <path v-else fill="currentColor" d="M7,15L12,10L17,15H7Z" />
              </svg>
            </button>
          </div>
        </div>

        <div v-if="isImporting && importProgress.total > 0" class="control-group import-progress">
          <span class="import-progress-label">{{ importProgress.phase }}</span>
          <span class="import-progress-count">{{ importProgress.current }} / {{ importProgress.total }}</span>
        </div>
        <div v-if="exportJobStatus === 'completed' && exportDownloadLinks.length > 1" class="control-group export-parts">
          <span class="export-parts-label">Export parts:</span>
          <button
            v-for="(link, idx) in exportDownloadLinks"
            :key="link.url"
            class="export-part-button"
            @click="downloadExportPart(link)"
          >
            Part {{ idx + 1 }}
          </button>
        </div>
        
      </div>

      <!-- Active filter tags -->
      <div v-if="filterTags.length" class="filter-tags-row">
        <span v-for="tag in filterTags" :key="tag" class="filter-tag">
          {{ tag }}
          <button class="filter-tag-remove" @click="removeFilterTag(tag)" title="Remove filter">×</button>
        </span>
      </div>

      <!-- Upload Progress Indicator -->
      <div v-if="uploadProgress.total > 0" class="upload-progress">
        <div class="progress-info">
          <span>Uploading: {{ uploadProgress.completed }}/{{ uploadProgress.total }}</span>
          <span v-if="uploadProgress.current"> - {{ uploadProgress.current }}</span>
        </div>
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: `${(uploadProgress.completed / uploadProgress.total) * 100}%` }"
          />
        </div>
      </div>

      <!-- Resume Job Dialog -->
      <div v-if="showResumeDialog && activeJobs.length > 0" class="resume-dialog-overlay" @click.self="showResumeDialog = false">
        <div class="resume-dialog">
          <div class="resume-dialog-header">
            <h3>Resume Upload Jobs</h3>
            <button class="close-button" @click="showResumeDialog = false" title="Close">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
              </svg>
            </button>
          </div>
          <div class="resume-dialog-content">
            <p v-if="activeJobs.length > 0" class="resume-info">
              Found {{ activeJobs.length }} incomplete upload job(s). You can resume them or cancel.
            </p>
            <div class="job-list">
              <div v-for="job in activeJobs" :key="job.jobId" class="job-item">
                <div class="job-info">
                  <div class="job-header">
                    <span class="job-name">{{ job.folderPath || 'Upload Job' }}</span>
                    <span class="job-status" :class="`status-${job.status}`">{{ job.status }}</span>
                  </div>
                  <div class="job-progress">
                    <span>{{ job.completedFiles }}/{{ job.totalFiles }} completed</span>
                    <span v-if="job.failedFiles > 0" class="failed-count">{{ job.failedFiles }} failed</span>
                  </div>
                  <div class="job-meta">
                    <span class="job-date">Started: {{ new Date(job.createdAt).toLocaleString() }}</span>
                    <span class="job-date">Updated: {{ new Date(job.updatedAt).toLocaleString() }}</span>
                  </div>
                </div>
                <div class="job-actions">
                  <button 
                    v-if="canResumeJob(job)"
                    class="resume-button" 
                    @click="resumeJob(job)"
                    :disabled="isIngesting"
                  >
                    Resume
                  </button>
                  <button 
                    v-if="job.status === 'active' || job.status === 'paused'"
                    class="cancel-button" 
                    @click="cancelJob(job.jobId)"
                  >
                    Cancel
                  </button>
                  <button 
                    v-if="job.status === 'completed' || job.status === 'failed'"
                    class="remove-button" 
                    @click="removeJob(job.jobId)"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="resume-dialog-footer">
            <button class="dismiss-button" @click="showResumeDialog = false">Dismiss</button>
          </div>
        </div>
      </div>

      <!-- Culling View Toggle -->
      <div v-if="showCullingView" class="culling-toggle">
        <button @click="showCullingView = false" class="back-to-gallery-button">
          ← Back to Gallery
        </button>
      </div>

      <!-- Main Content Layout -->
      <div class="main-content">
        <!-- Culling View -->
        <div v-if="showCullingView" class="culling-view">
          <div class="culling-controls">
            <input
              v-model="filterInput"
              type="text"
              class="search-input"
              placeholder="Filter images..."
              @keyup.enter="addFilterTag"
            />
            <select v-model="sortBy" @change="loadCullingImages" class="sort-select">
              <option value="ingestAt">Sort by Date</option>
              <option value="rating">Sort by Rating</option>
              <option value="basename">Sort by Name</option>
            </select>
          </div>
          <div class="culling-grid">
            <div 
              v-for="image in filteredCullingImages" 
              :key="image.id"
              class="culling-image-item"
            >
              <img :src="image.previewUrl || image.iconUrl || image.url" :alt="image.originalName" />
              <div class="culling-actions">
                <div class="rating-control">
                  <span 
                    v-for="star in 5" 
                    :key="star"
                    @click="updateRating(image.id, star)"
                    :class="{ active: image.rating && star <= image.rating }"
                  >
                    ★
                  </span>
                </div>
                <button @click="deleteImage(image)" class="reject-button">Reject</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Type Library (fonts) -->
        <div v-else-if="currentGalleryType === 'type-library'" class="gallery-images">
          <!-- Font filters -->
          <div class="type-library-filters">
            <div class="category-filters">
              <button
                class="category-pill"
                :class="{ active: fontCategoryFilter === null }"
                @click="fontCategoryFilter = null; loadFonts()"
              >All</button>
              <button
                class="category-pill"
                :class="{ active: fontFamilyView }"
                @click="fontFamilyView = !fontFamilyView"
              >Family</button>
            </div>
          </div>

          <div v-if="isLoadingFonts" class="loading-overlay">
            <n-spin size="large" />
          </div>
          <div v-else-if="galleryFonts.length === 0" class="empty-gallery">
            <n-empty description="No fonts in this type library yet" />
          </div>
          <!-- Family view -->
          <div v-else-if="fontFamilyView" class="images-grid">
            <div
              class="image-item font-item"
              v-for="family in fontFamilies"
              :key="family.familyName"
              @click="openFontFamilyModal(family)"
            >
              <div class="image-card">
                <img
                  v-if="family.representative.thumbnailUrl"
                  :src="family.representative.thumbnailUrl"
                  :alt="family.familyName"
                  class="image-preview"
                  @error="handleImageError"
                />
                <div v-else class="font-placeholder">
                  <span class="font-placeholder-name">{{ family.familyName }}</span>
                </div>
                <div class="font-card-info">
                  <span class="font-card-name">{{ family.familyName }}</span>
                  <span v-if="family.representative.category" class="font-card-category">{{ family.representative.category }}</span>
                </div>
                <span v-if="family.variants.length > 1" class="font-family-count">{{ family.variants.length }} styles</span>
                <div class="image-overlay">
                  <button class="bookmark-button" @click.stop="addFontToMoodboard(family.representative)" title="Add to moodboard">
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor" d="M17,3H7A2,2 0 0,0 5,5V21L12,18L19,21V5A2,2 0 0,0 17,3Z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- Flat view -->
          <div v-else class="images-grid">
            <div
              class="image-item font-item"
              v-for="font in galleryFonts"
              :key="font.id"
              @click="openFontModal(font)"
            >
              <div class="image-card">
                <img
                  v-if="font.thumbnailUrl"
                  :src="font.thumbnailUrl"
                  :alt="font.displayName"
                  class="image-preview"
                  @error="handleImageError"
                />
                <div v-else class="font-placeholder">
                  <span class="font-placeholder-name">{{ font.displayName }}</span>
                </div>
                <div class="font-card-info">
                  <span class="font-card-name">{{ font.displayName }}</span>
                  <span v-if="font.category" class="font-card-category">{{ font.category }}</span>
                </div>
                <div class="image-overlay">
                  <button class="bookmark-button" @click.stop="addFontToMoodboard(font)" title="Add to moodboard">
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor" d="M17,3H7A2,2 0 0,0 5,5V21L12,18L19,21V5A2,2 0 0,0 17,3Z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Gallery Images -->
        <div v-else class="gallery-images" ref="galleryImagesRef">
          <div v-if="isLoading" class="loading-overlay">
            <n-spin size="large" />
          </div>
          <div v-if="visibleImages.length === 0 && !isLoading" class="empty-gallery">
            <n-empty description="No images in gallery yet">
              <template #icon>
                <n-icon size="48">
                  <svg viewBox="0 0 24 24">
                    <path fill="currentColor" d="M4,6H20V16H4M20,18A2,2 0 0,0 22,16V6C22,4.89 21.1,4 20,4H4C2.89,4 2,4.89 2,6V16A2,2 0 0,0 4,18H0V20H24V18H20Z" />
                  </svg>
                </n-icon>
              </template>
            </n-empty>
          </div>
          <div v-else class="images-grid">
            <div
              class="image-item"
              v-for="image in visibleImages"
              :key="image.id"
              :class="{
                'selected': selectedImage?.id === image.id,
                'color-reference': colorSimilarityReferenceImageId === image.id
              }"
              @click="selectImage(image)"
            >
              <div class="image-card">
                <img
                  :src="getImageUrl(image)"
                  :alt="image.originalName"
                  class="image-preview"
                  @error="handleImageError"
                />
                <div class="image-overlay">
                  <button class="view-button" @click.stop="viewImage(image)" title="View full size">
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor" d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
                    </svg>
                  </button>
                  <button class="bookmark-button" @click.stop="addImageToSelectedMoodboard(image)" title="Add to moodboard">
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor" d="M17,3H7A2,2 0 0,0 5,5V21L12,18L19,21V5A2,2 0 0,0 17,3Z" />
                    </svg>
                  </button>
                  <button class="delete-button" @click.stop="deleteImage(image)" title="Delete image">
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Infinite scroll sentinel -->
          <div ref="scrollSentinelRef" class="scroll-sentinel">
            <n-spin v-if="isLoadingMore" size="small" />
          </div>
        </div>

        <!-- Upload Pane -->
        <div class="upload-pane" v-if="showUploadPane">
          <div class="upload-header">
            <h3 class="upload-title">Upload Images</h3>
            <button class="close-button" @click="closeUploadPane" title="Close upload" :disabled="isIngesting">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
              </svg>
            </button>
          </div>
          <div class="upload-content">
            <!-- Progress -->
            <div v-if="isIngesting && uploadProgress.total > 0" class="upload-progress-block">
              <div class="upload-spinner" aria-hidden="true"></div>
              <p class="upload-progress-text">
                Uploading {{ uploadProgress.completed }} / {{ uploadProgress.total }} image{{ uploadProgress.total !== 1 ? 's' : '' }}
              </p>
            </div>

            <!-- Dropzone -->
            <div
              class="upload-dropzone"
              :class="{ 'drag-over': isDropZoneDragOver, 'upload-dropzone-busy': isIngesting }"
              @dragover.prevent="isDropZoneDragOver = true"
              @dragleave.prevent="isDropZoneDragOver = false"
              @drop.prevent="handleUploadDrop"
            >
              <div class="drop-content">
                <svg viewBox="0 0 24 24" width="40" height="40" class="drop-icon">
                  <path fill="currentColor" d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" />
                </svg>
                <p class="drop-text">Drop images or folders here</p>
                <p class="drop-subtext">JPG, PNG, GIF, WebP — folder names become tags</p>
                <div class="drop-buttons">
                  <button class="browse-btn" :disabled="isIngesting" @click="handleBrowseFiles">
                    Browse Files
                  </button>
                  <button class="browse-btn folder-btn" :disabled="isIngesting" @click="handleSelectFolder">
                    Select Folder
                  </button>
                </div>
              </div>
            </div>

            <!-- Hidden file input -->
            <input
              ref="uploadFileInputRef"
              type="file"
              multiple
              accept="image/*"
              style="display: none"
              @change="handleBrowseFilesInput"
            />

            <div class="upload-actions">
              <button class="upload-btn-secondary" @click="closeUploadPane" :disabled="isIngesting">Close</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Add to Moodboard Dialog -->
      <n-modal v-model:show="showMoodboardDialog">
        <n-card
          style="width: 500px"
          title="Add to Moodboard"
          :bordered="false"
          size="huge"
          role="dialog"
          aria-modal="true"
        >
          <div v-if="availableMoodboards.length === 0" class="no-moodboards">
            <p>Select a project first.</p>
          </div>
          <div v-else>
            <n-select
              v-model:value="selectedMoodboardId"
              :options="availableMoodboards.map(m => ({ label: m.name, value: m.id }))"
              placeholder="Select moodboard"
            />
          </div>
          <template #footer>
            <div style="display: flex; justify-content: flex-end; gap: 12px">
              <n-button @click="showMoodboardDialog = false">Cancel</n-button>
              <n-button 
                type="primary" 
                @click="addImageToMoodboard" 
                :disabled="!selectedMoodboardId"
              >
                Add
              </n-button>
            </div>
          </template>
        </n-card>
      </n-modal>

      <!-- Image Modal -->
      <div v-if="showImageModal && modalImage" class="image-modal-overlay" @click="closeImageModal">
        <div class="image-modal" @click.stop>
          <div class="image-modal-header">
            <button class="image-modal-close" @click="closeImageModal" title="Close">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
              </svg>
            </button>
          </div>
          <div class="image-modal-content">
            <div class="image-modal-body">
              <!-- Image Section (75% width, 100% height) -->
              <div class="image-modal-image-section">
                <div class="image-modal-image-container" ref="imageModalRef">
                  <img 
                    :src="modalImage.url" 
                    :alt="modalImage.originalName"
                    class="image-modal-img"
                    @error="handleImageError"
                    @load="() => { updateFaceRectangles(); updateObjectRectangles(); updateTextRectangles(); }"
                  />
                  <!-- Face rectangles overlay -->
                  <template v-for="(person, index) in getModalPeople()" :key="`face-${index}`">
                    <div
                      v-if="visibleFaceIndices.includes(index)"
                      class="detection-rectangle"
                      :style="getFaceRectangleStyle(person, index)"
                    />
                  </template>
                  <!-- Object rectangles overlay -->
                  <template v-for="(obj, index) in getModalObjects()" :key="`object-${index}`">
                    <div
                      v-if="visibleObjectIndices.includes(index)"
                      class="detection-rectangle"
                      :style="getObjectRectangleStyle(obj, index)"
                    />
                  </template>
                  <!-- Text region rectangles overlay -->
                  <template v-for="(block, index) in getModalText()" :key="`text-${index}`">
                    <div
                      v-if="visibleTextIndices.includes(index)"
                      class="detection-rectangle text-detection-rectangle"
                      :style="getTextRectangleStyle(block, index)"
                    />
                  </template>
                </div>
              </div>
              
              <!-- Data Section (25% width) -->
              <div class="image-modal-data-section">
              <!-- Image Details -->
              <div class="image-modal-section">
                <h4 class="image-modal-section-title">{{ modalImage.subject || modalImage.originalName || modalImage.basename }}</h4>
                  <div class="image-modal-metadata">
                    <p class="metadata-line">
                      {{ formatFileSize(modalImage.size) }}<span v-if="modalImage.width && modalImage.height"> · {{ modalImage.width }} × {{ modalImage.height }}px</span>
                    </p>
                    <p v-if="modalImage.format || modalImage.createdAt" class="metadata-line">
                      <span v-if="modalImage.format">{{ modalImage.format }}</span><span v-if="modalImage.format && modalImage.createdAt"> · </span><span v-if="modalImage.createdAt">{{ formatDate(modalImage.createdAt) }}</span>
                    </p>
                    <p v-if="modalImage.uri" class="metadata-line">
                      <strong>Source URL:</strong> 
                      <a :href="modalImage.uri" target="_blank" rel="noopener noreferrer" class="metadata-link">
                        {{ modalImage.uri.length > 60 ? modalImage.uri.substring(0, 60) + '...' : modalImage.uri }}
                      </a>
                    </p>
                  </div>
                </div>

                <!-- Processing Menu -->
                <div v-if="modalImage" class="image-modal-section">
                  <h4 class="image-modal-section-title">Actions</h4>
                  <NDropdown
                    :options="getProcessingOptions(modalImage!)"
                    :disabled="isProcessing && processingImage?.id === modalImage!.id"
                    @select="(key: string) => handleProcessingAction(key, modalImage!)"
                    trigger="click"
                    placement="bottom-start"
                  >
                    <button 
                      class="process-dropdown-button modal-process-button" 
                      @click.stop
                      :disabled="isProcessing && processingImage?.id === modalImage!.id"
                      title="Process image"
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14">
                        <path fill="currentColor" d="M12,2A2,2 0 0,1 14,4A2,2 0 0,1 12,6A2,2 0 0,1 10,4A2,2 0 0,1 12,2M10.5,7H13.5L10.5,22H8.5L10.5,7M13.5,22L15.5,7H18.5L16.5,22H13.5Z" />
                      </svg>
                      <span>Process</span>
                      <svg viewBox="0 0 24 24" width="12" height="12" style="margin-left: 4px;">
                        <path fill="currentColor" d="M7,10L12,15L17,10H7Z" />
                      </svg>
                    </button>
                  </NDropdown>
                </div>

                <!-- Extension Metadata -->
                <div v-if="modalImage.uri || modalImage.provenance || modalImage.primaryLocation || (modalImage.keywords && (Array.isArray(modalImage.keywords) ? modalImage.keywords.length > 0 : true))" class="image-modal-section">
                  <div class="image-modal-metadata">
                    <p v-if="modalImage.uri" class="metadata-line">
                      <strong>Source URL:</strong> 
                      <a :href="modalImage.uri" target="_blank" rel="noopener noreferrer" class="metadata-link">
                        {{ modalImage.uri.length > 50 ? modalImage.uri.substring(0, 50) + '...' : modalImage.uri }}
                      </a>
                    </p>
                    <p v-if="modalImage.provenance?.pageUrl" class="metadata-line">
                      <strong>Page URL:</strong> 
                      <a :href="modalImage.provenance.pageUrl" target="_blank" rel="noopener noreferrer" class="metadata-link">
                        {{ modalImage.provenance.pageUrl.length > 50 ? modalImage.provenance.pageUrl.substring(0, 50) + '...' : modalImage.provenance.pageUrl }}
                      </a>
                    </p>
                    <p v-if="modalImage.provenance?.sourceSite" class="metadata-line">
                      <strong>Source Site:</strong> {{ modalImage.provenance.sourceSite }}
                    </p>
                    <p v-if="modalImage.primaryLocation" class="metadata-line">
                      <strong>Location:</strong> {{ modalImage.primaryLocation }}
                    </p>
                    <p v-if="modalImage.label" class="metadata-line">
                      <strong>Label:</strong> {{ modalImage.label }}
                    </p>
                  </div>
                </div>

                <!-- Provenance Details (Fonts, Designers, Formats) -->
                <div v-if="modalImage.provenance && (modalImage.provenance.fonts?.length || modalImage.provenance.designers?.length || modalImage.provenance.formats?.length)" class="image-modal-section">
                  <h4 class="image-modal-section-title">Provenance Details</h4>
                  <div class="image-modal-metadata">
                    <div v-if="modalImage.provenance.fonts?.length" class="metadata-subsection">
                      <strong>Fonts:</strong>
                      <ul class="metadata-list">
                        <li v-for="(font, index) in modalImage.provenance.fonts" :key="`font-${index}`">
                          {{ font.name || font }}
                          <span v-if="font.number" class="metadata-subtext"> (#{{ font.number }})</span>
                        </li>
                      </ul>
                    </div>
                    <div v-if="modalImage.provenance.designers?.length" class="metadata-subsection">
                      <strong>Designers:</strong>
                      <ul class="metadata-list">
                        <li v-for="(designer, index) in modalImage.provenance.designers" :key="`designer-${index}`">
                          {{ designer.name || designer }}
                        </li>
                      </ul>
                    </div>
                    <div v-if="modalImage.provenance.formats?.length" class="metadata-subsection">
                      <strong>Formats:</strong>
                      <ul class="metadata-list">
                        <li v-for="(format, index) in modalImage.provenance.formats" :key="`format-${index}`">
                          {{ format }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Color Palette -->
                <div v-if="getModalColorPalette()" class="image-modal-section">
                  <h4 class="image-modal-section-title">Color Palette</h4>
                  <div class="color-palette-container">
                    <!-- Dominant Color -->
                    <div 
                      v-if="getModalColorPalette()?.dominantColor?.hex" 
                      class="color-swatch"
                      :style="{ backgroundColor: getModalColorPalette()?.dominantColor?.hex }"
                      :title="getModalColorPalette()?.dominantColor?.hex"
                      @click="() => { const domColor = getModalColorPalette()?.dominantColor; if (domColor) handleColorSwatchClick(domColor); }"
                    >
                      <div class="color-label">{{ getModalColorPalette()?.dominantColor?.hex }}</div>
                    </div>
                    
                    <!-- Color Palette - 2 per row -->
                    <div 
                      v-for="(color, index) in getModalColorPalette()?.colors || []" 
                      :key="color.hex || index"
                      class="color-swatch"
                      :style="{ backgroundColor: color.hex }"
                      :title="`${color.hex || 'N/A'}${color.percentage ? ` (${color.percentage.toFixed(1)}%)` : ''}`"
                      @click="handleColorSwatchClick(color)"
                    >
                      <div class="color-label">{{ color.hex }}</div>
                    </div>
                  </div>
                  
                  <!-- Current Palette Selection -->
                  <div v-if="selectedProjectId && colorPalettes.length > 0" class="palette-selection" style="margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border-color);">
                    <label class="control-label" style="display: block; margin-bottom: 8px; font-size: 13px; font-weight: 500; color: var(--text-color);">Add Colors To:</label>
                    <select 
                      v-model="currentPaletteId" 
                      @change="saveCurrentPalette"
                      class="palette-select"
                      :disabled="!selectedProjectId || colorPalettes.length === 0"
                      style="width: 100%;"
                    >
                      <option value="">Select Palette</option>
                      <option 
                        v-for="palette in colorPalettes" 
                        :key="palette.id" 
                        :value="palette.id"
                      >
                        {{ palette.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Description -->
                <div v-if="getModalFullDescription()" class="image-modal-section">
                  <h4 class="image-modal-section-title">Description</h4>
                  <p class="description-text">{{ getModalFullDescription() }}</p>
                </div>

                <!-- Faces Detection -->
                <div v-if="getModalPeople()?.length" class="image-modal-section">
                  <div class="detection-section-header">
                    <h4 class="image-modal-section-title">Faces ({{ getModalPeople()?.length }})</h4>
                    <div class="detection-controls">
                      <button class="detection-control-btn" @click="showAllFaces">Show All</button>
                      <button class="detection-control-btn" @click="hideAllFaces">Hide All</button>
                    </div>
                  </div>
                  <div class="detection-tags-list">
                    <n-tag
                      v-for="(person, index) in getModalPeople()"
                      :key="index"
                      round
                      size="small"
                      :style="{
                        backgroundColor: visibleFaceIndices.includes(index) ? getTagColor(index) : 'transparent',
                        color: visibleFaceIndices.includes(index) ? '#fff' : getTagColor(index),
                        border: `1.5px solid ${getTagColor(index)}`,
                        margin: '2px',
                        cursor: 'pointer'
                      }"
                      @click="toggleFaceIndex(index)"
                    >
                      {{ person.name || `Person ${index + 1}` }}
                    </n-tag>
                  </div>
                </div>

                <!-- Objects Detection -->
                <div v-if="getModalObjects()?.length" class="image-modal-section">
                  <div class="detection-section-header">
                    <h4 class="image-modal-section-title">Objects ({{ getModalObjects()?.length }})</h4>
                    <div class="detection-controls">
                      <button class="detection-control-btn" @click="showAllObjects">Show All</button>
                      <button class="detection-control-btn" @click="hideAllObjects">Hide All</button>
                    </div>
                  </div>
                  <div class="detection-tags-list">
                    <n-tag
                      v-for="(obj, index) in getModalObjects()"
                      :key="index"
                      round
                      size="small"
                      :style="{
                        backgroundColor: visibleObjectIndices.includes(index) ? getTagColor(index) : 'transparent',
                        color: visibleObjectIndices.includes(index) ? '#fff' : getTagColor(index),
                        border: `1.5px solid ${getTagColor(index)}`,
                        margin: '2px',
                        cursor: 'pointer'
                      }"
                      @click="toggleObjectIndex(index)"
                    >
                      {{ obj.name || `Object ${index + 1}` }}
                    </n-tag>
                  </div>
                </div>

                <!-- Detected Text -->
                <div v-if="getModalText()?.length" class="image-modal-section">
                  <div class="detection-section-header">
                    <h4 class="image-modal-section-title">Text ({{ getModalText()?.length }} blocks)</h4>
                    <div class="detection-controls">
                      <button class="detection-control-btn" @click="showAllText">Show All</button>
                      <button class="detection-control-btn" @click="hideAllText">Hide All</button>
                    </div>
                  </div>
                  <div class="text-blocks-list">
                    <div
                      v-for="(block, index) in getModalText()"
                      :key="index"
                      class="text-block-item"
                      :class="{ 'text-block-item--active': visibleTextIndices.includes(index) }"
                      :style="{ borderLeftColor: getTagColor(index), backgroundColor: visibleTextIndices.includes(index) ? `${getTagColor(index)}22` : undefined }"
                      @click="toggleTextIndex(index)"
                      title="Click to toggle region overlay"
                    >
                      <span class="text-block-content">{{ block.text }}</span>
                      <span v-if="block.confidence" class="text-block-confidence">{{ Math.round(block.confidence * 100) }}%</span>
                    </div>
                  </div>
                </div>

                <!-- Keywords -->
                <div v-if="getModalKeywords()?.length" class="image-modal-section">
                  <h4 class="image-modal-section-title">Keywords ({{ getModalKeywords()?.length }})</h4>
                  <div class="keywords-list">
                    <n-tag 
                      v-for="(keyword, index) in getModalKeywords()" 
                      :key="`${keyword}-${index}`"
                      closable
                      round
                      size="small"
                      style="margin: 2px;"
                      @close="removeKeyword(keyword)"
                    >
                      {{ keyword }}
                    </n-tag>
                  </div>
                </div>

                <!-- Actions -->
                <div class="image-modal-actions">
                  <button 
                    class="image-modal-action-button" 
                    @click="handleDeleteFromModal" 
                    title="Delete image"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path fill="currentColor" d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                    </svg>
                    <span>Delete</span>
                  </button>
                  <button 
                    class="image-modal-action-button" 
                    @click="handleOpenInTab" 
                    title="Open in new tab"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path fill="currentColor" d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
                    </svg>
                    <span>Open in Tab</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Font Modal -->
    <div v-if="showFontModal && selectedFont" class="image-modal-overlay" @click="closeFontModal">
      <div class="image-modal font-modal" @click.stop>
        <div class="image-modal-header">
          <button class="image-modal-close" @click="closeFontModal" title="Close">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>
        <div class="image-modal-content">
          <div class="image-modal-body">
            <!-- Typeface images: top row = Aa (left) + sample images (right); landscape full width below -->
            <div class="image-modal-image-section">
              <div class="image-modal-image-container font-modal-gallery">
                <template v-if="fontModalHasAnyImage">
                  <div class="font-modal-images-scroll">
                    <div v-if="fontModalShowTopRow" class="font-modal-top-split">
                      <div class="font-modal-top-col font-modal-top-col--square">
                        <figure v-if="fontSquareSpecimenEntry" class="font-modal-figure font-modal-figure--square">
                          <figcaption class="font-modal-figure-label">{{ fontSquareSpecimenEntry.label }}</figcaption>
                          <img
                            :src="fontSquareSpecimenEntry.url"
                            :alt="`${selectedFont.displayName} — ${fontSquareSpecimenEntry.label}`"
                            class="image-modal-img font-specimen-img"
                            loading="lazy"
                          />
                        </figure>
                        <div v-else class="font-modal-pane-empty">No square specimen yet</div>
                      </div>
                      <div class="font-modal-top-col font-modal-top-col--samples">
                        <template v-if="fontUsageSampleEntries.length > 0">
                          <figure
                            v-for="(item, idx) in fontUsageSampleEntries"
                            :key="item.url + String(idx)"
                            class="font-modal-figure font-modal-figure--sample"
                          >
                            <figcaption class="font-modal-figure-label">{{ item.label }}</figcaption>
                            <img
                              :src="item.url"
                              :alt="`${selectedFont.displayName} — ${item.label}`"
                              class="image-modal-img font-specimen-img"
                              loading="lazy"
                            />
                          </figure>
                        </template>
                        <div v-else class="font-modal-pane-empty">No sample images</div>
                      </div>
                    </div>
                    <figure v-if="fontLandscapeSpecimenEntry" class="font-modal-figure font-modal-figure--landscape">
                      <figcaption class="font-modal-figure-label">{{ fontLandscapeSpecimenEntry.label }}</figcaption>
                      <img
                        :src="fontLandscapeSpecimenEntry.url"
                        :alt="`${selectedFont.displayName} — ${fontLandscapeSpecimenEntry.label}`"
                        class="image-modal-img font-specimen-img"
                        loading="lazy"
                      />
                    </figure>
                  </div>
                </template>
                <div v-else class="font-no-specimen">
                  <span>No specimen image yet</span>
                  <p style="font-size:12px; color:#666; margin-top: 8px;">Fonts are added via the server import script.</p>
                </div>
              </div>
            </div>

            <!-- Font metadata section (25%) -->
            <div class="image-modal-data-section">
              <!-- Font name rendered in the typeface -->
              <div class="image-modal-section">
                <h4 class="font-modal-name" :style="selectedFontWoff2Url ? { fontFamily: `'font-preview-${selectedFont.id}', serif` } : {}">{{ selectedFont.displayName }}</h4>
                <div class="font-badges">
                  <span v-if="selectedFont.category" class="font-badge category-badge">{{ selectedFont.category }}</span>
                  <span v-if="selectedFont.isWhitelisted" class="font-badge whitelisted-badge">✓ Whitelisted</span>
                </div>
              </div>

              <!-- Details -->
              <div class="image-modal-section">
                <div class="image-modal-metadata">
                  <p v-if="selectedFont.designer" class="metadata-line"><strong>Designer:</strong> {{ selectedFont.designer }}</p>
                  <p v-if="selectedFont.source" class="metadata-line"><strong>Source:</strong> {{ selectedFont.source }}</p>
                  <p v-if="selectedFont.url" class="metadata-line">
                    <strong>URL:</strong>
                    <a :href="selectedFont.url" target="_blank" rel="noopener" class="font-source-link">{{ selectedFont.url }}</a>
                  </p>
                  <p v-if="selectedFont.description" class="metadata-line font-description">{{ selectedFont.description }}</p>
                  <div v-if="fontWeights.length" class="metadata-line">
                    <strong>Weights:</strong>
                    <div class="font-weights-list">
                      <span v-for="w in fontWeights" :key="w" class="font-weight-pill">{{ w }}</span>
                    </div>
                  </div>
                  <div v-if="selectedFont.tags && selectedFont.tags.length" class="metadata-line">
                    <strong>Tags:</strong>
                    <div class="font-tags-list">
                      <span v-for="tag in selectedFont.tags" :key="tag" class="font-tag-pill">{{ tag }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AI Describe results preview -->
              <div v-if="fontDescribeResult" class="image-modal-section font-describe-result">
                <h4 class="image-modal-section-title">AI Suggestions</h4>
                <div class="image-modal-metadata">
                  <p v-if="fontDescribeResult.description" class="metadata-line font-description">{{ fontDescribeResult.description }}</p>
                  <p v-if="fontDescribeResult.category" class="metadata-line"><strong>Category:</strong> {{ fontDescribeResult.category }}</p>
                  <p v-if="fontDescribeResult.designer" class="metadata-line"><strong>Designer:</strong> {{ fontDescribeResult.designer }}</p>
                  <div v-if="fontDescribeResult.tags?.length" class="metadata-line">
                    <strong>Tags:</strong>
                    <div class="font-tags-list">
                      <span v-for="tag in fontDescribeResult.tags" :key="tag" class="font-tag-pill">{{ tag }}</span>
                    </div>
                  </div>
                </div>
                <div style="display: flex; gap: 8px; margin-top: 8px;">
                  <button class="font-action-btn font-action-btn--primary" @click="applyFontDescribeResult">Apply</button>
                  <button class="font-action-btn" @click="fontDescribeResult = null">Dismiss</button>
                </div>
              </div>

              <!-- Actions -->
              <div class="image-modal-section">
                <h4 class="image-modal-section-title">Actions</h4>
                <div style="display: flex; flex-direction: column; gap: 8px;">
                  <a
                    v-if="selectedFontWoff2Url"
                    :href="selectedFontWoff2Url"
                    download
                    class="font-action-btn"
                  >Download woff2</a>
                  <button
                    class="font-action-btn"
                    @click="addFontToMoodboard(selectedFont)"
                  >Add to Moodboard</button>
                  <button
                    class="font-action-btn"
                    :disabled="describingFont"
                    @click="describeFontWithAI(selectedFont)"
                  >{{ describingFont ? 'Describing…' : 'Describe with AI' }}</button>
                </div>
              </div>

              <!-- Image summary -->
              <div class="image-modal-section" style="color: #666; font-size: 12px;">
                <span v-if="fontModalHasAnyImage">
                  {{ fontModalTotalImageCount }} image{{ fontModalTotalImageCount !== 1 ? 's' : '' }} above
                  <template v-if="fontUsageImageCount"> ({{ fontUsageImageCount }} sample)</template>
                </span>
                <span v-else>No specimen or usage images yet</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Font Family Modal -->
    <div v-if="showFontFamilyModal && selectedFontFamily" class="image-modal-overlay" @click="closeFontFamilyModal">
      <div class="image-modal font-modal" @click.stop>
        <div class="image-modal-header">
          <button class="image-modal-close" @click="closeFontFamilyModal" title="Close">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>
        <div class="image-modal-content">
          <div class="image-modal-body">
            <!-- Specimen images section (reuses selectedFont computeds via watcher sync) -->
            <div class="image-modal-image-section">
              <div class="image-modal-image-container font-modal-gallery">
                <template v-if="fontModalHasAnyImage">
                  <div class="font-modal-images-scroll">
                    <div v-if="fontModalShowTopRow" class="font-modal-top-split">
                      <div class="font-modal-top-col font-modal-top-col--square">
                        <figure v-if="fontSquareSpecimenEntry" class="font-modal-figure font-modal-figure--square">
                          <figcaption class="font-modal-figure-label">{{ fontSquareSpecimenEntry.label }}</figcaption>
                          <img
                            :src="fontSquareSpecimenEntry.url"
                            :alt="`${selectedFamilyVariant?.displayName} — ${fontSquareSpecimenEntry.label}`"
                            class="image-modal-img font-specimen-img"
                            loading="lazy"
                          />
                        </figure>
                        <div v-else class="font-modal-pane-empty">No square specimen yet</div>
                      </div>
                      <div class="font-modal-top-col font-modal-top-col--samples">
                        <template v-if="fontUsageSampleEntries.length > 0">
                          <figure
                            v-for="(item, idx) in fontUsageSampleEntries"
                            :key="item.url + String(idx)"
                            class="font-modal-figure font-modal-figure--sample"
                          >
                            <figcaption class="font-modal-figure-label">{{ item.label }}</figcaption>
                            <img
                              :src="item.url"
                              :alt="`${selectedFamilyVariant?.displayName} — ${item.label}`"
                              class="image-modal-img font-specimen-img"
                              loading="lazy"
                            />
                          </figure>
                        </template>
                        <div v-else class="font-modal-pane-empty">No sample images</div>
                      </div>
                    </div>
                    <figure v-if="fontLandscapeSpecimenEntry" class="font-modal-figure font-modal-figure--landscape">
                      <figcaption class="font-modal-figure-label">{{ fontLandscapeSpecimenEntry.label }}</figcaption>
                      <img
                        :src="fontLandscapeSpecimenEntry.url"
                        :alt="`${selectedFamilyVariant?.displayName} — ${fontLandscapeSpecimenEntry.label}`"
                        class="image-modal-img font-specimen-img"
                        loading="lazy"
                      />
                    </figure>
                  </div>
                </template>
                <div v-else class="font-no-specimen">
                  <span>No specimen image yet</span>
                </div>
              </div>
            </div>

            <!-- Right panel: family info + style list -->
            <div class="image-modal-data-section">
              <div class="image-modal-section">
                <h4 class="font-modal-name" :style="selectedFamilyVariantWoff2Url ? { fontFamily: `'font-preview-${selectedFamilyVariant.id}', serif` } : {}">
                  {{ selectedFamilyVariant?.displayName || selectedFontFamily.familyName }}
                </h4>
                <div class="font-badges">
                  <span v-if="selectedFamilyVariant?.category" class="font-badge category-badge">{{ selectedFamilyVariant.category }}</span>
                  <span class="font-badge" style="background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.6);">{{ selectedFontFamily.variants.length }} styles</span>
                </div>
              </div>

              <!-- Style list -->
              <div class="image-modal-section">
                <h4 class="image-modal-section-title">Styles</h4>
                <div class="font-family-style-list">
                  <button
                    v-for="(variant, idx) in selectedFontFamily.variants"
                    :key="variant.id"
                    class="font-family-style-item"
                    :class="{ active: idx === selectedFamilyVariantIdx }"
                    :style="fontWoff2Src(variant) ? { fontFamily: `'font-preview-${variant.id}', serif` } : {}"
                    @click="selectedFamilyVariantIdx = idx"
                  >{{ variant.displayName }}</button>
                </div>
              </div>

              <!-- Actions -->
              <div class="image-modal-section">
                <h4 class="image-modal-section-title">Actions</h4>
                <div style="display: flex; flex-direction: column; gap: 8px;">
                  <a
                    v-if="selectedFamilyVariantWoff2Url"
                    :href="selectedFamilyVariantWoff2Url"
                    download
                    class="font-action-btn"
                  >Download woff2</a>
                  <button
                    class="font-action-btn"
                    @click="addFontToMoodboard(selectedFamilyVariant)"
                  >Add to Moodboard</button>
                  <button
                    class="font-action-btn"
                    :disabled="describingFamily"
                    @click="describeFamilyWithAI"
                  >{{ describingFamily ? 'Describing…' : 'Describe Family with AI' }}</button>
                </div>
              </div>

              <!-- AI Suggestions (family) -->
              <div v-if="familyDescribeResult" class="image-modal-section font-describe-result">
                <h4 class="image-modal-section-title">AI Suggestions</h4>
                <div class="image-modal-metadata">
                  <p v-if="familyDescribeResult.description" class="metadata-line font-description">
                    {{ familyDescribeResult.description }}
                  </p>
                  <p v-if="familyDescribeResult.category" class="metadata-line">
                    <strong>Category:</strong> {{ familyDescribeResult.category }}
                  </p>
                  <p v-if="familyDescribeResult.designer" class="metadata-line">
                    <strong>Designer:</strong> {{ familyDescribeResult.designer }}
                  </p>
                  <div v-if="familyDescribeResult.tags?.length" class="metadata-line">
                    <strong>Tags:</strong>
                    <div class="font-tags-list">
                      <span v-for="tag in familyDescribeResult.tags" :key="tag" class="font-tag-pill">{{ tag }}</span>
                    </div>
                  </div>
                </div>
                <div style="display: flex; gap: 8px; margin-top: 8px;">
                  <button class="font-action-btn font-action-btn--primary" @click="applyFamilyDescribeResult">
                    Apply to All {{ selectedFontFamily.variants.length }} Styles
                  </button>
                  <button class="font-action-btn" @click="familyDescribeResult = null">Dismiss</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add to Gallery Dialog -->
    <n-modal v-model:show="showAddToGalleryDialog" preset="card" title="Add Image to Gallery" style="width: 500px">
      <div v-if="selectedImageForGallery" class="add-to-gallery-dialog">
        <p style="margin-bottom: 16px;">
          Add <strong>{{ selectedImageForGallery.originalName }}</strong> to:
        </p>
        <n-select
          v-model:value="selectedGalleryForAdd"
          multiple
          filterable
          :options="availableGalleries.map(g => ({ label: g.name, value: g.id }))"
          placeholder="Select galleries..."
        />
        <div style="margin-top: 16px; display: flex; justify-content: flex-end; gap: 8px;">
          <n-button @click="showAddToGalleryDialog = false">Cancel</n-button>
          <n-button type="primary" @click="addImageToGalleries">Add to Galleries</n-button>
        </div>
      </div>
    </n-modal>

    <!-- View queue modal: jobs waiting or in progress (DB is source of truth; queues are in-memory) -->
    <n-modal v-model:show="showQueueModal" preset="card" title="Processing queue" style="width: 560px;">
      <div class="queue-modal-body">
        <p v-if="queueJobs.length === 0 && !queueJobsLoading" class="queue-empty">No jobs waiting or in progress.</p>
        <p v-else-if="queueJobsLoading && queueJobs.length === 0" class="queue-loading">Loading…</p>
        <ul v-else class="queue-job-list">
          <li v-for="job in queueJobs" :key="job.id" class="queue-job-item">
            <img
              v-if="job.refImage?.iconUrl"
              :src="job.refImage.iconUrl"
              alt=""
              class="queue-job-thumb"
            />
            <span v-else class="queue-job-thumb queue-job-thumb-placeholder">—</span>
            <span class="queue-job-name">{{ job.refImage?.basename || job.refImageId }}</span>
            <span class="queue-job-type">{{ job.jobType }}</span>
            <span class="queue-job-status" :class="job.status.toLowerCase()">{{ job.status }}</span>
          </li>
        </ul>
        <p class="queue-modal-hint">Jobs are stored in the database. If the server restarts, in‑memory queues are cleared but pending jobs remain here until re-queued.</p>
      </div>
    </n-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useMessage, NDropdown, NTag, NModal, NCard, NSelect, NButton } from 'naive-ui'
import GallerySelector from '../GallerySelector.vue'
import { apiClient, type ColorPaletteResponse } from '@/services/api'
import { selectFolder, scanDirectoryHandle } from '@/utils/folderScanner'
import { fontWoff2Src } from '@/utils/fontUtils'
import { generateMediumThumbnails } from '@/utils/thumbnailGenerator'
import { UploadQueue, type UploadItem, type UploadProgress } from '@/utils/uploadQueue'
import {
  createUploadJob,
  loadAllActiveJobs,
  updateJobFileStatus,
  updateJobStatus,
  deleteJob,
  getJobProgress,
  type UploadJob,
} from '@/utils/persistentUploadQueue'
import {
  canResumeJob,
  prepareJobForResume,
  reReadJobFiles,
} from '@/utils/jobResumer'
import { useGalleryExport } from '@/composables/useGalleryExport'
import { useGalleryImport } from '@/composables/useGalleryImport'

const message = useMessage()
const {
  isExporting,
  exportError,
  exportJobStatus,
  exportDownloadLinks,
  exportGallery,
  downloadExportPart,
} = useGalleryExport()
const {
  isImporting,
  importError,
  importProgress,
  importFromFolder,
} = useGalleryImport()

// Gallery image interface
// Supports both legacy Image model and new RefImage model fields
interface GalleryImage {
  id: string // SHA1 hash from database
  filename: string
  originalName: string
  basename?: string // Display name (from RefImage model)
  mimeType: string
  entities?: string | { people: any[]; objects: any[] } // JSON string or parsed object
  size: number
  url: string // Full-size S3 URL
  // Thumbnails
  iconUrl?: string
  previewUrl?: string
  iconWidth?: number
  iconHeight?: number
  previewWidth?: number
  previewHeight?: number
  // Technical metadata
  width?: number
  height?: number
  aspectRatio?: number
  format?: string
  // AI metadata (legacy - for backward compatibility)
  subject?: string
  description?: string // Legacy field - prefer shortDescription or fullDescription
  keywords?: string | string[] // JSON array string or parsed array
  dominantColors?: string // Legacy JSON array - prefer dominantColor and palette
  // New RefImage fields
  shortDescription?: string // Brief description from BLIP
  fullDescription?: string // General description from Ollama
  dominantColor?: string // Single hex color code (e.g., "#FF5733")
  palette?: string // JSON array of hex codes (e.g., ["#FF5733", "#00FF00", ...])
  rating?: number | null // 1-5 star rating
  // Extension metadata fields
  uri?: string // Original source URL from extension
  primaryLocation?: string // Location from extension or AI analysis
  provenance?: any // Provenance object with pageUrl, sourceSite, fonts, designers, formats
  label?: string // Label from extension
  // Detection fields (JSON strings from database)
  faces?: string // JSON array of detected faces with name, rect, and confidence
  people?: string // Legacy field - JSON array of detected people (deprecated, use faces)
  objects?: string // JSON array of detected objects with name, rect, and confidence
  text?: string // JSON array of detected text blocks with rect and text content
  // Ollama structured data fields (JSON strings from database)
  captions?: string // JSON string of captions array
  scene?: string // JSON string of scene object
  aesthetics?: string // JSON string of aesthetics object
  safety?: string // JSON string of safety object
  textSummary?: string // JSON string of textSummary object
  // Other metadata
  layoutData?: string | any // JSON string or parsed object
  detectionData?: string | any // JSON string or parsed object
  // Organization
  brandId?: string
  projectId?: string
  tags?: string // JSON array
  // Timestamps
  createdAt: string
  updatedAt: string
  // Soft delete flag
  deleted?: boolean
  // Galleries
  galleries?: Array<{ id: string; name: string }>
  // UI state
  uploadId?: string // Temp ID for processing
}

// Gallery state
const galleryImages = ref<GalleryImage[]>([])
const selectedImage = ref<GalleryImage | null>(null)
const showUploadPane = ref(false)
const isDropZoneDragOver = ref(false)
const uploadFileInputRef = ref<HTMLInputElement | null>(null)
const isProcessing = ref(false)
const processingImage = ref<GalleryImage | null>(null)
const imageResults = ref<Record<string, {
  description?: string
  colorPalette?: ColorPaletteResponse
  yoloDetection?: {
    success: boolean
    objects: {
      detections: Array<{
        name: string
        confidence: number
        bbox: { x: number; y: number; width: number; height: number }
      }>
      total: number
      processingTime: number
    }
    faces: {
      detections: Array<{
        confidence: number
        bbox: { x: number; y: number; width: number; height: number }
        landmarks?: any
      }>
      total: number
      processingTime: number
    } | null
    processingTime: number
    model: string
    device: string
  }
  layoutData?: any
  detectionData?: any
  fullAnalysis?: {
    description: string
    layoutData?: any
    detectionData?: any
    model?: string
    processingTime?: number
  }
  ollamaStructured?: {
    fullDescription?: string
    subject?: string
    captions?: Array<{
      text: string
      model: string
      confidence: number
    }>
    scene?: {
      indoorOutdoor?: string
      locations?: string[]
      primaryLocation?: string
      timeOfDay?: string
      weather?: string
      setting?: string[]
    }
    aesthetics?: {
      style?: string[]
      mood?: string[]
      qualityScore?: number
      sharpness?: number
      noiseLevel?: number
    }
    safety?: {
      nsfw?: number
      violence?: number
      medical?: number
      disallowed?: boolean
    }
    keywords?: string[]
    textSummary?: {
      hasText: boolean
      totalChars: number
      lines: string[]
    }
  }
  openAI?: {
    description: string
    keywords: string[]
    model: 'gpt-5.2' | 'gpt-5-mini'
    usedFallback: boolean
    processingTime: number
    promptLevel: string
    data: any
  }
}>>({})


// Queue modal (view jobs waiting / in progress; DB is source of truth)
const showQueueModal = ref(false)
const queueJobs = ref<Array<{
  id: string
  refImageId: string
  jobType: string
  status: string
  refImage?: { id: string; basename: string; iconUrl: string | null }
}>>([])
const queueJobsLoading = ref(false)
let queueModalPollTimer: ReturnType<typeof setInterval> | null = null

async function loadQueueJobs() {
  queueJobsLoading.value = true
  try {
    const res = await apiClient.getImageProcessingJobs({
      activeOnly: true,
      _t: Date.now(),
    })
    queueJobs.value = res.jobs || []
  } catch (e) {
    console.warn('Failed to load queue jobs', e)
    queueJobs.value = []
  } finally {
    queueJobsLoading.value = false
  }
}

function openQueueModal() {
  showQueueModal.value = true
}

window.addEventListener('open-queue-modal', openQueueModal)

// Modal state
const showImageModal = ref(false)
const modalImage = ref<GalleryImage | null>(null)
const visibleFaceIndices = ref<number[]>([])
const visibleObjectIndices = ref<number[]>([])
const visibleTextIndices = ref<number[]>([])
const imageModalRef = ref<HTMLImageElement | null>(null)

// Pagination state
const currentPage = ref(1)
const pageSize = ref(40)
const hasMore = ref(false)
const totalImages = ref(0)
const isLoading = ref(false)
const isLoadingMore = ref(false)
const scrollSentinelRef = ref<HTMLElement | null>(null)
const galleryImagesRef = ref<HTMLElement | null>(null)

// Filter state
const filterInput = ref('')
const filterTags = ref<string[]>([])
const sortBy = ref<'createdAt' | 'filename' | 'colorSimilarity'>('createdAt')
const sortOrder = ref<'asc' | 'desc'>('desc')
const selectedBrandId = ref<string | undefined>()
const selectedProjectId = ref<string | undefined>()
const colorSimilarityReferenceImageId = ref<string | null>(null)
const currentGalleryId = ref<string | null>(null)
const currentGalleryType = ref<'images' | 'type-library'>('images')
const includeGalleries = ref(true) // Include gallery info in image responses

// Type Library state
const galleryFonts = ref<any[]>([])
const selectedFont = ref<any | null>(null)
const showFontModal = ref(false)
const fontCategoryFilter = ref<string | null>(null)
const fontSearch = ref('')
const isLoadingFonts = ref(false)
const fontFamilyView = ref(true)

// Font family state
interface FontFamily {
  familyName: string
  representative: any
  variants: any[]
}
const selectedFontFamily = ref<FontFamily | null>(null)
const showFontFamilyModal = ref(false)
const selectedFamilyVariantIdx = ref(0)

const selectedFamilyVariant = computed(() =>
  selectedFontFamily.value?.variants[selectedFamilyVariantIdx.value] ?? null
)


const loadFonts = async () => {
  if (!currentGalleryId.value) return
  isLoadingFonts.value = true
  try {
    const response = await apiClient.fetchGalleryFonts(currentGalleryId.value, {
      category: fontCategoryFilter.value || undefined,
      search: fontSearch.value || undefined,
    })
    galleryFonts.value = response.fonts
  } catch (error: any) {
    message.error(`Failed to load fonts: ${error.message || 'Unknown error'}`)
  } finally {
    isLoadingFonts.value = false
  }
}

function pickMostRegular(variants: any[]): any {
  const normalStyle = variants.filter(v => !v.style || v.style === 'normal')
  const pool = normalStyle.length ? normalStyle : variants
  const weightScore = (v: any): number => Math.abs(parseInt(v.weight || '400', 10) - 400)
  return pool.reduce((best: any, v: any) => weightScore(v) < weightScore(best) ? v : best, pool[0])
}

const fontFamilies = computed((): FontFamily[] => {
  const groups = new Map<string, any[]>()
  for (const font of galleryFonts.value) {
    const key = font.fontFamily?.trim() || font.displayName
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(font)
  }
  return Array.from(groups.entries()).map(([familyName, variants]) => ({
    familyName,
    representative: pickMostRegular(variants),
    variants,
  }))
})

const openFontFamilyModal = (family: FontFamily) => {
  selectedFontFamily.value = family
  selectedFamilyVariantIdx.value = family.variants.indexOf(family.representative)
  showFontFamilyModal.value = true
  // inject @font-face for all variants so style list renders in the actual typefaces
  for (const v of family.variants) {
    const src = fontWoff2Src(v)
    if (src) {
      const styleId = `font-preview-style-${v.id}`
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style')
        style.id = styleId
        style.textContent = `@font-face { font-family: 'font-preview-${v.id}'; src: url('${src}') format('woff2'); }`
        document.head.appendChild(style)
      }
    }
  }
  // sync selectedFont so specimen computeds work
  selectedFont.value = family.representative
}

const closeFontFamilyModal = () => {
  showFontFamilyModal.value = false
  selectedFontFamily.value = null
  selectedFamilyVariantIdx.value = 0
  selectedFont.value = null
  familyDescribeResult.value = null
}

const describingFamily = ref(false)
const familyDescribeResult = ref<{ description: string; category: string; tags: string[]; designer: string } | null>(null)

const describeFamilyWithAI = async () => {
  const family = selectedFontFamily.value
  if (!family || describingFamily.value) return
  describingFamily.value = true
  familyDescribeResult.value = null
  try {
    const { suggestions } = await apiClient.describeFontFamily(
      family.familyName,
      family.variants.map((v: any) => ({ id: v.id, displayName: v.displayName, weight: v.weight, style: v.style })),
      family.representative?.url ?? null,
    )
    familyDescribeResult.value = suggestions
  } catch (err) {
    console.error('Font family describe failed', err)
  } finally {
    describingFamily.value = false
  }
}

const applyFamilyDescribeResult = async () => {
  const family = selectedFontFamily.value
  if (!family || !familyDescribeResult.value) return
  const updates: Record<string, any> = {}
  if (familyDescribeResult.value.description) updates.description = familyDescribeResult.value.description
  if (familyDescribeResult.value.category) updates.category = familyDescribeResult.value.category
  if (familyDescribeResult.value.designer) updates.designer = familyDescribeResult.value.designer
  if (familyDescribeResult.value.tags?.length) updates.tags = familyDescribeResult.value.tags
  try {
    await Promise.all(
      family.variants.map(async (variant: any) => {
        const { font: updated } = await apiClient.updateFont(variant.id, updates)
        Object.assign(variant, updated)
      })
    )
    familyDescribeResult.value = null
  } catch (err) {
    console.error('Font family update failed', err)
  }
}

// Keep selectedFont in sync when cycling variants in family modal
watch(selectedFamilyVariantIdx, (idx) => {
  if (selectedFontFamily.value) {
    selectedFont.value = selectedFontFamily.value.variants[idx] ?? null
  }
})


const selectedFontWoff2Url = computed(() => fontWoff2Src(selectedFont.value))
const selectedFamilyVariantWoff2Url = computed(() => fontWoff2Src(selectedFamilyVariant.value))

/** Normalize usage URLs whether the API sent an array or a JSON string. */
function normalizeFontUsageUrls(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((u): u is string => typeof u === 'string' && u.length > 0)
  }
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw) as unknown
      return Array.isArray(p)
        ? p.filter((u): u is string => typeof u === 'string' && u.length > 0)
        : []
    } catch {
      return []
    }
  }
  return []
}

const openFontModal = (font: any) => {
  selectedFont.value = font
  showFontModal.value = true
  // Dynamically inject @font-face so the font name renders in the actual typeface
  const woffSrc = fontWoff2Src(font)
  if (woffSrc) {
    const styleId = `font-preview-style-${font.id}`
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `@font-face { font-family: 'font-preview-${font.id}'; src: url('${woffSrc}') format('woff2'); }`
      document.head.appendChild(style)
    }
  }
}

const closeFontModal = () => {
  showFontModal.value = false
  selectedFont.value = null
  fontDescribeResult.value = null
}

const describingFont = ref(false)
const fontDescribeResult = ref<{ description: string; category: string; tags: string[]; designer: string } | null>(null)

const describeFontWithAI = async (font: any) => {
  if (!font || describingFont.value) return
  describingFont.value = true
  fontDescribeResult.value = null
  try {
    const { suggestions } = await apiClient.describeFont(font.id)
    fontDescribeResult.value = suggestions
  } catch (err) {
    console.error('Font describe failed', err)
  } finally {
    describingFont.value = false
  }
}

const applyFontDescribeResult = async () => {
  if (!selectedFont.value || !fontDescribeResult.value) return
  const updates: Record<string, any> = {}
  if (fontDescribeResult.value.description) updates.description = fontDescribeResult.value.description
  if (fontDescribeResult.value.category) updates.category = fontDescribeResult.value.category
  if (fontDescribeResult.value.designer) updates.designer = fontDescribeResult.value.designer
  if (fontDescribeResult.value.tags?.length) updates.tags = fontDescribeResult.value.tags
  try {
    const { font: updated } = await apiClient.updateFont(selectedFont.value.id, updates)
    // Patch selectedFont in-place so the modal reflects the new values immediately
    Object.assign(selectedFont.value, updated)
    fontDescribeResult.value = null
  } catch (err) {
    console.error('Font update failed', err)
  }
}

const fontSquareSpecimenEntry = computed((): { url: string; label: string } | null => {
  const f = selectedFont.value
  if (!f?.thumbnailUrl) return null
  return { url: f.thumbnailUrl, label: 'Square specimen (Aa)' }
})

const fontLandscapeSpecimenEntry = computed((): { url: string; label: string } | null => {
  const f = selectedFont.value
  if (!f?.thumbnailLandscapeUrl) return null
  return { url: f.thumbnailLandscapeUrl, label: 'Landscape specimen' }
})

const fontUsageSampleEntries = computed((): { url: string; label: string }[] => {
  const f = selectedFont.value
  if (!f) return []
  const usage = normalizeFontUsageUrls(f.usageImageUrls)
  return usage.map((url, i) => ({
    url,
    label: usage.length > 1 ? `Sample image ${i + 1}` : 'Sample image',
  }))
})

const fontModalHasAnyImage = computed(
  () =>
    !!(
      fontSquareSpecimenEntry.value ||
      fontLandscapeSpecimenEntry.value ||
      fontUsageSampleEntries.value.length > 0
    ),
)

/** Top row only when there is something to put in the split (Aa and/or samples). */
const fontModalShowTopRow = computed(
  () => !!fontSquareSpecimenEntry.value || fontUsageSampleEntries.value.length > 0,
)

const fontModalTotalImageCount = computed(() => {
  let n = 0
  if (fontSquareSpecimenEntry.value) n++
  n += fontUsageSampleEntries.value.length
  if (fontLandscapeSpecimenEntry.value) n++
  return n
})

const fontUsageImageCount = computed((): number => {
  if (!selectedFont.value) return 0
  return normalizeFontUsageUrls(selectedFont.value.usageImageUrls).length
})

const fontWeights = computed((): string[] => {
  if (!selectedFont.value) return []
  const meta = selectedFont.value.metadata
  if (meta) {
    try {
      const parsed = typeof meta === 'string' ? JSON.parse(meta) : meta
      if (Array.isArray(parsed?.weights)) return parsed.weights
    } catch {}
  }
  return []
})

const addFontToMoodboard = async (font: any) => {
  if (!currentMoodboardIdForBookmark.value) {
    message.warning('Select a moodboard first')
    return
  }
  try {
    await apiClient.addFontToMoodboard(currentMoodboardIdForBookmark.value, font.id)
    message.success(`Added "${font.displayName}" to moodboard`)
  } catch (error: any) {
    message.error(`Failed to add to moodboard: ${error.message || 'Unknown error'}`)
  }
}

// Color palette state
const colorPalettes = ref<Array<{ id: string; name: string }>>([])
const currentPaletteId = ref<string>('')

// Import file input (fallback when folder picker not available)
const importFileInputRef = ref<HTMLInputElement | null>(null)

// Ingest state
const isIngesting = ref(false)
const showCullingView = ref(false)

const thumbnailUploadQueue = ref<UploadQueue | null>(null)
const originalUploadQueue = ref<UploadQueue | null>(null)
const uploadProgress = ref<UploadProgress>({ total: 0, completed: 0, failed: 0 })
const originalFilesMap = new Map<string, File>() // Store original files for background upload
const cullingImages = ref<GalleryImage[]>([])
const currentJobId = ref<string | null>(null)
const activeJobs = ref<UploadJob[]>([])
const showResumeDialog = ref(false)
const directoryHandleRef = ref<any>(null) // Store directory handle for resuming

/** Parse keywords from a gallery image (JSON string or array). */
function getImageKeywords(img: GalleryImage): string[] {
  if (!img.keywords) return []
  if (Array.isArray(img.keywords)) return img.keywords
  try {
    const parsed = typeof img.keywords === 'string' ? JSON.parse(img.keywords) : img.keywords
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

/** True if image has at least one keyword that starts with the given text (case-insensitive). */
function imageMatchesKeywordPrefix(img: GalleryImage, prefix: string): boolean {
  if (!prefix.trim()) return true
  const keywords = getImageKeywords(img)
  const lower = prefix.trim().toLowerCase()
  return keywords.some(k => typeof k === 'string' && k.toLowerCase().startsWith(lower))
}

function addFilterTag() {
  const val = filterInput.value.trim()
  if (val && !filterTags.value.includes(val)) {
    filterTags.value.push(val)
  }
  filterInput.value = ''
}

function removeFilterTag(tag: string) {
  filterTags.value = filterTags.value.filter(t => t !== tag)
}

// Computed: Filter out deleted images and apply keyword-prefix filter for display
const visibleImages = computed(() => {
  const list = galleryImages.value.filter(img => !img.deleted)
  const activeTags = filterInput.value.trim()
    ? [...filterTags.value, filterInput.value.trim()]
    : filterTags.value
  if (!activeTags.length) return list
  return list.filter(img => activeTags.every(tag => imageMatchesKeywordPrefix(img, tag)))
})

// Computed: Culling view filtered by keyword prefix (same rule as visibleImages)
const filteredCullingImages = computed(() => {
  const activeTags = filterInput.value.trim()
    ? [...filterTags.value, filterInput.value.trim()]
    : filterTags.value
  if (!activeTags.length) return cullingImages.value
  return cullingImages.value.filter(img => activeTags.every(tag => imageMatchesKeywordPrefix(img, tag)))
})

// Computed: Check if there are any deleted images
const hasDeletedImages = computed(() => {
  return galleryImages.value.some(img => img.deleted)
})

// Handle gallery selection change
const handleGalleryChange = (gallery: any) => {
  currentGalleryType.value = gallery?.galleryType === 'type-library' ? 'type-library' : 'images'
  if (currentGalleryType.value === 'type-library') {
    galleryFonts.value = []
    loadFonts()
  } else {
    refreshGallery()
  }
}

// Load images from database (with localStorage cache)
const loadImagesFromDatabase = async (append = false) => {
  if (append) {
    isLoadingMore.value = true
  } else {
    isLoading.value = true
  }
  try {
    const response = await apiClient.fetchImages({
      page: currentPage.value,
      limit: pageSize.value,
      sortBy: sortBy.value,
      sortOrder: sortOrder.value,
      brandId: selectedBrandId.value,
      projectId: selectedProjectId.value,
      colorSimilarityImageId: colorSimilarityReferenceImageId.value || undefined,
      galleryIds: currentGalleryId.value ? [currentGalleryId.value] : undefined,
      includeGalleries: includeGalleries.value,
    })

    if (append) {
      galleryImages.value = [...galleryImages.value, ...response.images]
    } else {
      galleryImages.value = response.images
    }
    hasMore.value = currentPage.value < response.pagination.totalPages
    totalImages.value = response.pagination.total

    if (!append) {
      cacheImagesToStorage()
    }
  } catch (error) {
    console.error('Failed to load images from database:', error)
    message.error('Failed to load images')

    if (!append) {
      // Fallback to localStorage cache
      loadImagesFromCache()
    }
  } finally {
    isLoading.value = false
    isLoadingMore.value = false
  }
}

// Cache images to localStorage
const cacheImagesToStorage = () => {
  try {
    const cacheData = {
      images: galleryImages.value,
      timestamp: Date.now(),
      page: currentPage.value,
    }
    localStorage.setItem('galleryImagesCache', JSON.stringify(cacheData))
  } catch (error) {
    console.error('Failed to cache images:', error)
  }
}

// Load images from localStorage cache (fallback)
const loadImagesFromCache = () => {
  try {
    const cached = localStorage.getItem('galleryImagesCache')
    if (cached) {
      const cacheData = JSON.parse(cached)
      // Only use cache if less than 5 minutes old
      if (Date.now() - cacheData.timestamp < 5 * 60 * 1000) {
        galleryImages.value = cacheData.images
        currentPage.value = cacheData.page
        message.info('Loaded from cache')
      }
    }
  } catch (error) {
    console.error('Failed to load from cache:', error)
  }
}

// Load images only when component is mounted (tab is active)
onMounted(() => {
  // Load selected project from localStorage if available
  const savedBrandId = localStorage.getItem('selectedBrandId')
  if (savedBrandId) {
    const savedProjectId = localStorage.getItem(`selectedProject_${savedBrandId}`)
    if (savedProjectId) {
      selectedProjectId.value = savedProjectId
    }
  }
  
  loadImagesFromDatabase()
  loadColorPalettes()
  loadAvailableMoodboards()
  window.addEventListener('keydown', handleEscapeKey)
})

// Infinite scroll
let scrollObserver: IntersectionObserver | null = null

const loadMoreImages = async () => {
  if (!hasMore.value || isLoadingMore.value || isLoading.value) return
  currentPage.value++
  await loadImagesFromDatabase(true)
}

const setupScrollObserver = () => {
  if (scrollObserver) scrollObserver.disconnect()
  if (!scrollSentinelRef.value || !galleryImagesRef.value) return
  scrollObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        loadMoreImages()
      }
    },
    { root: galleryImagesRef.value, rootMargin: '200px' }
  )
  scrollObserver.observe(scrollSentinelRef.value)
}

// Set up observer once both refs are available
watch([scrollSentinelRef, galleryImagesRef], ([sentinel, container]) => {
  if (sentinel && container) setupScrollObserver()
})

// Watch for project changes to reload palettes
watch(selectedProjectId, () => {
  loadColorPalettes()
})

// Gallery actions
const refreshGallery = async () => {
  currentPage.value = 1
  galleryImages.value = []
  await loadImagesFromDatabase()
}

async function handleExportGallery() {
  if (!currentGalleryId.value) return
  await exportGallery(currentGalleryId.value)
  if (exportError.value) {
    message.error(exportError.value)
  } else if (exportJobStatus.value === 'completed') {
    if (exportDownloadLinks.value.length <= 1) {
      message.success('Gallery exported and downloaded.')
    } else {
      message.success(`Part 1 downloaded. Download remaining ${exportDownloadLinks.value.length - 1} part(s) below.`)
    }
  } else if (!isExporting.value) {
    message.success('Gallery exported and downloaded.')
  }
}

async function handleImportGallery() {
  if ('showDirectoryPicker' in window) {
    const result = await importFromFolder(
      selectedBrandId.value,
      undefined,
      currentGalleryId.value
    )
    finishImport(result)
  } else {
    importFileInputRef.value?.click()
  }
}

async function handleImportFileInput(event: Event) {
  const input = event.target as HTMLInputElement
  const fileList = input.files
  if (!fileList?.length) return
  const files = Array.from(fileList).map((file) => ({ file, name: file.name }))
  input.value = ''
  const result = await importFromFolder(
    selectedBrandId.value,
    async () => files,
    currentGalleryId.value
  )
  finishImport(result)
}

function finishImport(result: { galleryId: string; name: string } | null) {
  if (importError.value) {
    message.error(importError.value)
  } else if (result) {
    message.success(`Gallery "${result.name}" imported.`)
    currentGalleryId.value = result.galleryId
    refreshGallery()
  }
}

const confirmClearDatabase = () => {
  if (confirm('⚠️ WARNING: This will delete ALL images from the database.\n\nThis action cannot be undone.\n\nAre you sure you want to continue?')) {
    clearDatabase()
  }
}

const clearDatabase = async () => {
  try {
    isLoading.value = true
    const result = await apiClient.clearAllImages()
    message.success(result.message)
    
    // Clear local state
    galleryImages.value = []
    selectedImage.value = null
    totalImages.value = 0
    hasMore.value = false
    currentPage.value = 1
    
    // Clear localStorage cache
    localStorage.removeItem('galleryImagesCache')
    
    console.log('Database cleared:', result)
  } catch (error) {
    console.error('Failed to clear database:', error)
    message.error('Failed to clear database')
  } finally {
    isLoading.value = false
  }
}

const selectImage = (image: GalleryImage) => {
  selectedImage.value = image
}

const viewImage = (image: GalleryImage) => {
  // Find the image in the current gallery to ensure we have the latest reference
  const currentImage = galleryImages.value.find(img => img.id === image.id) || image
  modalImage.value = currentImage
  showImageModal.value = true
  visibleFaceIndices.value = [] // Reset overlays when opening new image
  visibleObjectIndices.value = []
  visibleTextIndices.value = []
  
  console.log('viewImage - opened modal:', {
    imageId: currentImage.id,
    hasYoloDetection: !!imageResults.value[currentImage.id]?.yoloDetection,
    yoloDetection: imageResults.value[currentImage.id]?.yoloDetection
  })
}

const closeImageModal = () => {
  showImageModal.value = false
  modalImage.value = null
  visibleFaceIndices.value = [] // Reset overlays when closing
  visibleObjectIndices.value = []
  visibleTextIndices.value = []
}

// Handle Escape key to close any open modal/overlay (priority: most specific first)
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  if (showImageModal.value) {
    closeImageModal()
  } else if (showFontModal.value) {
    closeFontModal()
  } else if (showFontFamilyModal.value) {
    closeFontFamilyModal()
  } else if (showResumeDialog.value) {
    showResumeDialog.value = false
  } else if (showCullingView.value) {
    showCullingView.value = false
  }
}

watch(showQueueModal, (isOpen) => {
  if (isOpen) {
    void loadQueueJobs()
    queueModalPollTimer = setInterval(() => { void loadQueueJobs() }, 2000)
  } else {
    if (queueModalPollTimer) {
      clearInterval(queueModalPollTimer)
      queueModalPollTimer = null
    }
  }
})

// Check for active jobs on mount
const checkActiveJobs = () => {
  const jobs = loadAllActiveJobs()
  activeJobs.value = jobs
  if (jobs.length > 0) {
    showResumeDialog.value = true
  }
}

// Clean up listener on component unmount
onUnmounted(() => {
  window.removeEventListener('keydown', handleEscapeKey)
  // Clean up upload queues
  thumbnailUploadQueue.value?.clear()
  originalUploadQueue.value?.clear()
  scrollObserver?.disconnect()
})

// Check for active jobs when component mounts
checkActiveJobs()

// Resume an existing upload job
const resumeJob = async (job: UploadJob) => {
  try {
    isIngesting.value = true
    currentJobId.value = job.jobId
    
    // Check if we can resume
    if (!canResumeJob(job)) {
      message.warning('This job cannot be resumed')
      return
    }
    
    // Get pending files
    const { pendingFiles } = prepareJobForResume(job)
    
    if (pendingFiles.length === 0) {
      message.info('No pending files to upload')
      updateJobStatus(job.jobId, 'completed')
      return
    }
    
    message.info(`Resuming upload: ${pendingFiles.length} file(s) remaining`)
    
    // If using File System Access API, prompt user to re-select folder
    let fileEntries: Array<{ file: File; relativePath: string }> = []
    let directoryHandle: any = null
    
    if (job.useFileSystemAPI && 'showDirectoryPicker' in window) {
      try {
        directoryHandle = await (window as any).showDirectoryPicker()
        directoryHandleRef.value = directoryHandle
        
        // Re-read files from directory
        const fileMap = await reReadJobFiles(job, directoryHandle)
        
        // Match pending files with re-read files
        fileEntries = pendingFiles
          .map(jobFile => {
            const file = fileMap.get(jobFile.relativePath)
            if (!file) {
              console.warn(`File not found: ${jobFile.relativePath}`)
              return null
            }
            return { file, relativePath: jobFile.relativePath }
          })
          .filter((entry): entry is { file: File; relativePath: string } => entry !== null)
      } catch (error: any) {
        if (error.name === 'AbortError') {
          message.info('Folder selection cancelled')
          return
        }
        throw error
      }
    } else {
      // Fallback: prompt user to select folder again
      message.warning('Please select the same folder to resume upload')
      const selected = await selectFolder()
      
      // Match files by relative path
      const selectedMap = new Map(selected.map(e => [e.relativePath, e.file]))
      fileEntries = pendingFiles
        .map(jobFile => {
          const file = selectedMap.get(jobFile.relativePath)
          if (!file) {
            console.warn(`File not found: ${jobFile.relativePath}`)
            return null
          }
          return { file, relativePath: jobFile.relativePath }
        })
        .filter((entry): entry is { file: File; relativePath: string } => entry !== null)
    }
    
    if (fileEntries.length === 0) {
      message.warning('No matching files found. Please ensure you select the same folder.')
      return
    }
    
    // Update job status to active
    updateJobStatus(job.jobId, 'active')
    
    if (!currentGalleryId.value) {
      message.warning('Select a gallery first, then resume.')
      return
    }
    await startUploadProcess(
      fileEntries,
      job.jobId,
      job.brandId || selectedBrandId.value,
      job.projectId || selectedProjectId.value || undefined,
      currentGalleryId.value
    )
  } catch (error) {
    console.error('Resume failed:', error)
    message.error(`Resume failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    if (currentJobId.value) {
      updateJobStatus(currentJobId.value, 'paused')
    }
  } finally {
    isIngesting.value = false
    showResumeDialog.value = false
  }
}

// Start upload process (shared between new ingest and resume). targetGalleryId is the gallery to add images to (required).
const startUploadProcess = async (
  fileEntries: Array<{ file: File; relativePath: string }>,
  jobId: string | null = null,
  brandId?: string,
  projectId?: string,
  targetGalleryId?: string | null
) => {
  const galleryId = targetGalleryId ?? currentGalleryId.value
  if (!galleryId) {
    message.error('No gallery selected. Select a gallery and try again.')
    return
  }
  // Create job if not resuming
  if (!jobId) {
    const folderPath = fileEntries[0]?.relativePath.substring(0, fileEntries[0].relativePath.lastIndexOf('/')) || ''
    const job = createUploadJob(fileEntries, {
      brandId,
      projectId,
      folderPath,
      useFileSystemAPI: 'showDirectoryPicker' in window,
      directoryHandle: directoryHandleRef.value,
    })
    jobId = job.jobId
    currentJobId.value = jobId
  }
  
  message.info(`Found ${fileEntries.length} image(s), starting upload...`)
  
  // Initialize upload queues
  // Rate limit: 100 uploads/min per user, so we use 4 files per batch with 3s delay = ~80/min (20% cushion)
  uploadProgress.value = { total: 0, completed: 0, failed: 0 }
  
  thumbnailUploadQueue.value = new UploadQueue(
    { batchSize: 4, delayBetweenBatches: 3000 }, // 4 files every 3s = ~80/min (under 100/min limit)
    (progress) => {
      console.log('[Upload Progress]', progress)
      uploadProgress.value = { ...progress }
      
      // Update job progress
      if (jobId) {
        const jobProgress = getJobProgress(jobId)
        if (jobProgress) {
          // Progress is tracked per-file in updateJobFileStatus
        }
      }
    }
  )
  
  originalUploadQueue.value = new UploadQueue(
    { batchSize: 4, delayBetweenBatches: 3000 } // Same rate limiting for original uploads
  )
  
  const uploadedImageIds: string[] = []   // newly uploaded images
  const crossGalleryImageIds: string[] = [] // duplicates from other galleries to link

  // Set upload functions with job tracking
  thumbnailUploadQueue.value.setUploadFunction(async (item: UploadItem) => {
    const relativePath = (item as any).relativePath || item.file.name
    const folderPath = relativePath.substring(0, relativePath.lastIndexOf('/')) || ''

    // Update job file status to uploading
    if (jobId) {
      updateJobFileStatus(jobId, relativePath, { status: 'uploading' })
    }

    try {
      const result = await apiClient.batchUploadImages(
        [item.file],
        true,
        brandId,
        projectId,
        folderPath
      )

      const uploadResult = result.results[0]

      // Image already exists somewhere — link it to this gallery instead of re-uploading
      if (uploadResult?.duplicate) {
        const existingId = uploadResult.imageId
        if (existingId) {
          crossGalleryImageIds.push(existingId)
          if (jobId) {
            updateJobFileStatus(jobId, relativePath, {
              status: 'completed',
              imageId: existingId,
              uploadedAt: new Date().toISOString(),
              duplicate: true,
            })
          }
        }
        return uploadResult
      }
      
      if (uploadResult?.imageId) {
        const imageId = uploadResult.imageId
        uploadedImageIds.push(imageId)
        originalFilesMap.set(imageId, item.file)
        
        // Update job file status to completed
        if (jobId) {
          updateJobFileStatus(jobId, relativePath, {
            status: 'completed',
            imageId,
            uploadedAt: new Date().toISOString(),
          })
        }
        
        return uploadResult
      }
      throw new Error('Upload failed: No imageId returned')
    } catch (error) {
      // Update job file status to failed
      if (jobId) {
        updateJobFileStatus(jobId, relativePath, {
          status: 'failed',
          error: error instanceof Error ? error.message : 'Unknown error',
          retries: ((item as any).retries || 0) + 1,
        })
      }
      throw error
    }
  })
  
  originalUploadQueue.value.setUploadFunction(async (item: UploadItem) => {
    if (!item.imageId) throw new Error('No imageId provided')
    const file = originalFilesMap.get(item.imageId)
    if (!file) throw new Error('Original file not found')
    return await apiClient.uploadOriginalImage(item.imageId, file)
  })
  
  // Generate thumbnails
  const files = fileEntries.map(entry => entry.file)
  const thumbnailFiles = await generateMediumThumbnails(files).catch(() => files)
  
  // Add files to thumbnail upload queue
  const uploadItems: UploadItem[] = fileEntries.map((entry, index) => ({
    file: thumbnailFiles[index] || entry.file,
    relativePath: entry.relativePath,
    retries: 0,
    status: 'pending' as const,
  }))
  
  console.log('[Ingest] Adding', uploadItems.length, 'files to upload queue')
  thumbnailUploadQueue.value.addItems(uploadItems)
  const initialProgress = thumbnailUploadQueue.value.getProgress()
  uploadProgress.value = { ...initialProgress }
  
  // Wait for thumbnail uploads to complete
  while (thumbnailUploadQueue.value.isQueueProcessing()) {
    await new Promise(resolve => setTimeout(resolve, 1000))
  }
  
  // Queue analysis jobs for newly uploaded images only
  if (uploadedImageIds.length > 0) {
    await apiClient.batchAnalyzeImages(uploadedImageIds)
  }

  // Add all images (new uploads + cross-gallery duplicates) to this gallery
  const allIdsToLink = [...uploadedImageIds, ...crossGalleryImageIds]
  if (allIdsToLink.length > 0) {
    try {
      await apiClient.addImagesToGallery(galleryId, allIdsToLink)
    } catch (error) {
      console.error('[Upload] Failed to add images to gallery:', error)
      message.warning('Images uploaded but failed to add to current gallery')
    }
  }

  // Start background original uploads
  const originalUploadItems: UploadItem[] = Array.from(originalFilesMap.entries()).map(([imageId, file]) => ({
    file,
    imageId,
    retries: 0,
    status: 'pending' as const,
  }))

  originalUploadQueue.value.addItems(originalUploadItems)

  // Reload gallery
  await refreshGallery()

  // Mark job as completed
  if (jobId) {
    updateJobStatus(jobId, 'completed')
    activeJobs.value = loadAllActiveJobs()
  }

  // Build success message
  const parts: string[] = []
  if (uploadedImageIds.length > 0) parts.push(`${uploadedImageIds.length} uploaded`)
  if (crossGalleryImageIds.length > 0) parts.push(`${crossGalleryImageIds.length} linked from library`)
  message.success(`Upload complete! ${parts.join(', ')}`)
}


// Cancel a job
const cancelJob = (jobId: string) => {
  updateJobStatus(jobId, 'cancelled')
  deleteJob(jobId)
  activeJobs.value = loadAllActiveJobs()
  message.info('Job cancelled')
}

// Delete a completed job
const removeJob = (jobId: string) => {
  deleteJob(jobId)
  activeJobs.value = loadAllActiveJobs()
  message.info('Job removed')
}

const loadCullingImages = async () => {
  try {
    const response = await apiClient.getCullingImages({
      page: 1,
      limit: 100,
      sortBy: 'ingestAt',
      sortOrder: 'desc',
    })
    cullingImages.value = response.images.map(img => ({
      id: img.id,
      filename: img.basename,
      originalName: img.basename,
      mimeType: `image/${img.format.toLowerCase()}`,
      size: 0,
      url: img.url,
      previewUrl: img.previewUrl,
      iconUrl: img.iconUrl,
      width: img.width,
      height: img.height,
      format: img.format,
      dominantColor: img.dominantColor || undefined,
      palette: img.palette ? (Array.isArray(img.palette) ? JSON.stringify(img.palette) : img.palette) : undefined,
      shortDescription: img.shortDescription || undefined,
      fullDescription: img.fullDescription || undefined,
      keywords: Array.isArray(img.keywords) ? JSON.stringify(img.keywords) : (img.keywords || '[]'),
      rating: img.rating ?? undefined,
      createdAt: img.ingestAt || new Date().toISOString(),
      updatedAt: img.ingestAt || new Date().toISOString(),
    }))
  } catch (error) {
    console.error('Failed to load culling images:', error)
    message.error('Failed to load culling images')
  }
}

const updateRating = async (imageId: string, rating: number) => {
  try {
    await apiClient.updateImageRating(imageId, rating)
    // Update local state
    const image = cullingImages.value.find(img => img.id === imageId)
    if (image) {
      image.rating = rating
    }
    message.success(`Rated ${rating} star${rating > 1 ? 's' : ''}`)
  } catch (error) {
    console.error('Failed to update rating:', error)
    message.error('Failed to update rating')
  }
}

// Rename image (uses BLIP to generate name)
const renameImage = async (image: GalleryImage) => {
  try {
    message.info('Generating name from image...')
    
    // Call API to rename (BLIP will generate the name)
    const result = await apiClient.renameImage(image.id)
    
    // Update local state
    const imageIndex = galleryImages.value.findIndex(img => img.id === image.id)
    if (imageIndex !== -1) {
      galleryImages.value[imageIndex].filename = result.newBasename
      galleryImages.value[imageIndex].originalName = result.newBasename
    }
    
    // Also update in cullingImages if it exists there
    const cullingIndex = cullingImages.value.findIndex(img => img.id === image.id)
    if (cullingIndex !== -1) {
      cullingImages.value[cullingIndex].filename = result.newBasename
      cullingImages.value[cullingIndex].originalName = result.newBasename
    }
    
    // Update selected/modal image if it's the same
    if (selectedImage.value?.id === image.id) {
      selectedImage.value.filename = result.newBasename
      selectedImage.value.originalName = result.newBasename
    }
    if (modalImage.value?.id === image.id) {
      modalImage.value.filename = result.newBasename
      modalImage.value.originalName = result.newBasename
    }
    
    message.success(`Renamed to ${result.newBasename} (${result.blipWord})`)
  } catch (error) {
    console.error('Failed to rename image:', error)
    message.error('Failed to rename image')
  }
}

const handleDeleteFromModal = async () => {
  if (modalImage.value) {
    closeImageModal()
    await deleteImage(modalImage.value)
  }
}

const handleOpenInTab = () => {
  if (modalImage.value) {
    window.open(modalImage.value.url, '_blank')
  }
}


const deleteImage = async (image: GalleryImage) => {
  try {
    await apiClient.deleteImage(image.id)
    
    // Mark as deleted in local array instead of removing
    const index = galleryImages.value.findIndex(img => img.id === image.id)
    if (index > -1) {
      galleryImages.value[index].deleted = true
      message.success('Image marked as deleted')
    }
    
    // Clear selection if this was the selected image
    if (selectedImage.value?.id === image.id) {
      selectedImage.value = null
    }
    
    // Close modal if viewing this image
    if (modalImage.value?.id === image.id) {
      closeImageModal()
    }
  } catch (error) {
    console.error('Failed to delete image:', error)
    message.error('Failed to delete image')
  }
}

const restoreTrash = async () => {
  try {
    isLoading.value = true
    const result = await apiClient.restoreAllImages()
    
    message.success(result.message || `Restored ${result.restoredCount} image(s)`)
    
    // Update local state - mark all deleted images as not deleted
    galleryImages.value.forEach(img => {
      if (img.deleted) {
        img.deleted = false
      }
    })

    // Refresh gallery
    await refreshGallery()
  } catch (error) {
    console.error('Failed to restore images:', error)
    message.error('Failed to restore images')
  } finally {
    isLoading.value = false
  }
}

const emptyTrash = async () => {
  if (!confirm('⚠️ WARNING: This will permanently delete all images marked for deletion.\n\nThis action cannot be undone.\n\nAre you sure you want to continue?')) {
    return
  }
  
  try {
    isLoading.value = true
    const result = await apiClient.emptyTrash()
    
    message.success(result.message || `Permanently deleted ${result.deletedCount} image(s) and ${result.filesDeleted} file(s)`)
    
    if (result.errors && result.errors.length > 0) {
      console.warn('Some files failed to delete:', result.errors)
      message.warning(`Some files failed to delete. Check console for details.`)
    }
    
    // Remove deleted images from local array
    galleryImages.value = galleryImages.value.filter(img => !img.deleted)
    
    // Refresh gallery
    await refreshGallery()
  } catch (error) {
    console.error('Failed to empty trash:', error)
    message.error('Failed to empty trash')
  } finally {
    isLoading.value = false
  }
}

// Upload pane actions
const toggleUploadPane = () => {
  if (!currentGalleryId.value) {
    message.warning('Select a gallery first, then upload images into it.')
    return
  }
  showUploadPane.value = !showUploadPane.value
  if (showUploadPane.value) {
    selectedImage.value = null // Close selection if open
  }
}

const closeUploadPane = () => {
  if (!isIngesting.value) showUploadPane.value = false
}

// Unified upload handlers — all paths go through startUploadProcess (tags + analysis)
const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.webp', '.tiff', '.tif', '.bmp'])
const isImageFileByName = (name: string) => IMAGE_EXTENSIONS.has(name.toLowerCase().substring(name.lastIndexOf('.')))

async function scanDroppedEntry(
  entry: any,
  parentPath: string,
  results: Array<{ file: File; relativePath: string }>
): Promise<void> {
  if (entry.isFile) {
    const file: File = await new Promise((res, rej) => entry.file(res, rej))
    if (isImageFileByName(file.name)) {
      results.push({ file, relativePath: parentPath ? `${parentPath}/${file.name}` : file.name })
    }
  } else if (entry.isDirectory) {
    const dirPath = parentPath ? `${parentPath}/${entry.name}` : entry.name
    const reader = entry.createReader()
    // readEntries returns max 100 at a time; loop until empty
    let batch: any[] = []
    do {
      batch = await new Promise<any[]>((res, rej) => reader.readEntries(res, rej))
      for (const child of batch) await scanDroppedEntry(child, dirPath, results)
    } while (batch.length > 0)
  }
}

const handleUploadDrop = async (e: DragEvent) => {
  isDropZoneDragOver.value = false
  if (!currentGalleryId.value) {
    message.warning('Select a gallery first.')
    return
  }
  if (isIngesting.value) return

  const items = e.dataTransfer?.items
  if (!items) return

  const entries: Array<{ file: File; relativePath: string }> = []
  const promises: Promise<void>[] = []
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.kind === 'file') {
      const entry = item.webkitGetAsEntry()
      if (entry) promises.push(scanDroppedEntry(entry, '', entries))
    }
  }
  await Promise.all(promises)

  if (entries.length === 0) return
  isIngesting.value = true
  try {
    await startUploadProcess(entries, null, selectedBrandId.value, selectedProjectId.value || undefined, currentGalleryId.value)
  } catch (err) {
    message.error(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
  } finally {
    isIngesting.value = false
  }
}

const handleBrowseFiles = () => {
  uploadFileInputRef.value?.click()
}

const handleBrowseFilesInput = async (e: Event) => {
  const input = e.target as HTMLInputElement
  const files = Array.from(input.files || []).filter(f => isImageFileByName(f.name))
  input.value = ''
  if (files.length === 0 || !currentGalleryId.value) return
  const entries = files.map(f => ({ file: f, relativePath: f.name }))
  isIngesting.value = true
  try {
    await startUploadProcess(entries, null, selectedBrandId.value, selectedProjectId.value || undefined, currentGalleryId.value)
  } catch (err) {
    message.error(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
  } finally {
    isIngesting.value = false
  }
}

const handleSelectFolder = async () => {
  if (!currentGalleryId.value) {
    message.warning('Select a gallery first.')
    return
  }
  isIngesting.value = true
  try {
    let fileEntries: Array<{ file: File; relativePath: string }> = []
    if ('showDirectoryPicker' in window) {
      try {
        const dirHandle = await (window as any).showDirectoryPicker()
        directoryHandleRef.value = dirHandle
        fileEntries = await scanDirectoryHandle(dirHandle, [], dirHandle.name)
      } catch (err: any) {
        if (err.name === 'AbortError') return
        fileEntries = await selectFolder()
      }
    } else {
      fileEntries = await selectFolder()
    }
    if (fileEntries.length === 0) return
    await startUploadProcess(fileEntries, null, selectedBrandId.value, selectedProjectId.value || undefined, currentGalleryId.value)
  } catch (err) {
    message.error(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
  } finally {
    isIngesting.value = false
  }
}

// Processing functions
const describeImage = async (image: GalleryImage) => {
  if (!image) return
  
  // Use database image ID (not uploadId, as gallery images are already in DB)
  const imageIdentifier = image.id
  if (!imageIdentifier) {
    message.error('No image identifier available')
    return
  }
  
  isProcessing.value = true
  processingImage.value = image
  
  try {
    console.log('Describing image:', imageIdentifier)
    // Submit job and get jobId
    const jobResponse = await apiClient.describeImage(imageIdentifier)
    const jobId = jobResponse.jobId
    
    if (!jobId) {
      throw new Error('No job ID returned from server')
    }
    
    // Poll for job status with exponential backoff
    let attempts = 0
    const maxAttempts = 300 // 5 minutes max
    let delay = 1000 // Start with 1 second
    let consecutiveErrors = 0

    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, delay))
      attempts++

      try {
        const statusResponse = await apiClient.getJobStatus(jobId)
        consecutiveErrors = 0
        
        if (statusResponse.status === 'completed') {
          // Try multiple ways to extract description from structured response
          let description = ''
          
          // First try: direct description field (from API)
          if (statusResponse.description && typeof statusResponse.description === 'string') {
            description = statusResponse.description.trim()
          }
          // Second try: structured response captions
          else if (statusResponse.result && typeof statusResponse.result === 'object') {
            const result = statusResponse.result as any
            // Check for structured response format
            if (result.captions && Array.isArray(result.captions) && result.captions.length > 0) {
              description = result.captions[0]?.text || ''
            }
            // Check for legacy response format
            else if (result.response && typeof result.response === 'string') {
              description = result.response.trim()
            }
            // Check for fullDescription field
            else if (result.fullDescription && typeof result.fullDescription === 'string') {
              description = result.fullDescription.trim()
            }
          }
          // Third try: fullDescription at top level
          else if (statusResponse.fullDescription && typeof statusResponse.fullDescription === 'string') {
            description = statusResponse.fullDescription.trim()
          }
          // Fourth try: result.response (legacy)
          else if (statusResponse.result?.response && typeof statusResponse.result.response === 'string') {
            description = statusResponse.result.response.trim()
          }
          
          // Check if description is empty
          if (!description) {
            console.warn('Description is empty:', { 
              statusResponse,
              hasDescription: !!statusResponse.description,
              hasResult: !!statusResponse.result,
              resultType: statusResponse.result ? typeof statusResponse.result : 'none',
              resultKeys: statusResponse.result && typeof statusResponse.result === 'object' ? Object.keys(statusResponse.result) : [],
              hasFullDescription: !!statusResponse.fullDescription
            })
            message.warning('Image description was generated but is empty. The model may not have produced any output.')
            return
          }
          
          // Store results in local state
          if (!imageResults.value[image.id]) {
            imageResults.value[image.id] = {}
          }
          imageResults.value[image.id].description = description
          
          // Store full structured response if available
          if (statusResponse.result && typeof statusResponse.result === 'object' && statusResponse.result.captions) {
            const result = statusResponse.result as any
            // Extract keywords from structured response
            const keywords: string[] = [
              ...(result.scene?.locations || []),
              ...(result.scene?.setting || []),
              ...(result.aesthetics?.style || []),
              ...(result.aesthetics?.mood || [])
            ]
            
            imageResults.value[image.id].ollamaStructured = {
              fullDescription: description,
              subject: description.substring(0, 100),
              captions: result.captions || [],
              scene: {
                indoorOutdoor: result.scene?.indoorOutdoor,
                locations: result.scene?.locations || [],
                primaryLocation: result.scene?.locations?.[0],
                timeOfDay: result.scene?.timeOfDay,
                weather: result.scene?.weather,
                setting: result.scene?.setting || []
              },
              aesthetics: {
                style: result.aesthetics?.style || [],
                mood: result.aesthetics?.mood || [],
                qualityScore: result.aesthetics?.qualityScore,
                sharpness: result.aesthetics?.sharpness,
                noiseLevel: result.aesthetics?.noiseLevel
              },
              safety: {
                nsfw: result.safety?.nsfw,
                violence: result.safety?.violence,
                medical: result.safety?.medical,
                disallowed: result.safety?.disallowed
              },
              keywords: keywords,
              textSummary: result.textSummary
            }
          }
          
          // Update the image object in the gallery to reflect the new description
          const imageIndex = galleryImages.value.findIndex(img => img.id === image.id)
          if (imageIndex > -1) {
            galleryImages.value[imageIndex].description = description
          }
          
          // If this is the selected image, update it too
          if (selectedImage.value?.id === image.id) {
            selectedImage.value.description = description
          }
          
          // Refresh from database to ensure we have the latest data
          await refreshGallery()

          message.success('Image description generated and saved')
          return
        } else if (statusResponse.status === 'failed') {
          throw new Error(statusResponse.error || 'Image description failed')
        }
        
        // Exponential backoff: 1s, 2s, 4s, then cap at 4s
        if (delay < 4000) {
          delay = Math.min(delay * 2, 4000)
        }
      } catch (statusError) {
        // If it's a 404, job might not exist yet, continue polling
        if (statusError instanceof Error && statusError.message.includes('404')) {
          consecutiveErrors = 0
          continue
        }
        consecutiveErrors++
        console.warn('Error checking job status:', statusError)
        if (consecutiveErrors >= 5) {
          throw new Error('Lost connection to worker — unable to check job status')
        }
      }
    }
    
    throw new Error('Image description timed out')
  } catch (error) {
    message.error('Failed to describe image')
    console.error(error)
  } finally {
    isProcessing.value = false
    processingImage.value = null
  }
}

const describeImageWithOpenAI = async (image: GalleryImage, promptLevel: 'fast' | 'medium' | 'detailed' = 'medium') => {
  if (!image) return
  
  // Use database image ID (not uploadId, as gallery images are already in DB)
  const imageIdentifier = image.id
  if (!imageIdentifier) {
    message.error('No image identifier available')
    return
  }
  
  isProcessing.value = true
  processingImage.value = image
  
  try {
    console.log('Describing image with OpenAI:', imageIdentifier, 'promptLevel:', promptLevel)
    
    const response = await apiClient.describeImageWithOpenAI(imageIdentifier, promptLevel, false)
    
    console.log('OpenAI description response:', {
      description: response.description,
      keywords: response.keywords,
      model: response.model,
      usedFallback: response.usedFallback,
      processingTime: response.processingTime
    })
    
    // Store results in local state
    if (!imageResults.value[image.id]) {
      imageResults.value[image.id] = {}
    }
    imageResults.value[image.id].description = response.description
    imageResults.value[image.id].openAI = {
      description: response.description,
      keywords: response.keywords,
      model: response.model,
      usedFallback: response.usedFallback,
      processingTime: response.processingTime,
      promptLevel: response.promptLevel,
      data: response.data
    }
    
    // Update the image object in the gallery
    const imageIndex = galleryImages.value.findIndex(img => img.id === image.id)
    if (imageIndex > -1) {
      galleryImages.value[imageIndex].description = response.description
      if (response.keywords && response.keywords.length > 0) {
        const existing: string[] = (() => { try { return JSON.parse((galleryImages.value[imageIndex].keywords as string) || '[]') } catch { return [] } })()
        const merged = Array.from(new Set([...existing, ...response.keywords].map(k => k.toLowerCase().trim()).filter(Boolean)))
        galleryImages.value[imageIndex].keywords = JSON.stringify(merged)
      }
    }

    // If this is the selected image, update it too
    if (selectedImage.value?.id === image.id) {
      selectedImage.value.description = response.description
      if (response.keywords && response.keywords.length > 0) {
        const existing: string[] = (() => { try { return JSON.parse((selectedImage.value.keywords as string) || '[]') } catch { return [] } })()
        const merged = Array.from(new Set([...existing, ...response.keywords].map(k => k.toLowerCase().trim()).filter(Boolean)))
        selectedImage.value.keywords = JSON.stringify(merged)
      }
    }
    
    // Refresh from database to ensure we have the latest data
    await refreshGallery()

    const modelInfo = response.usedFallback ? ' (using GPT-5-mini fallback)' : ` (${response.model})`
    message.success(`Image description generated${modelInfo}`)
  } catch (error: any) {
    // Extract error message from various possible response formats
    const errorData = error.response?.data
    const errorMessage = errorData?.error?.message || errorData?.message || errorData?.error || error.message || 'Unknown error'
    console.error('OpenAI description error:', {
      error,
      status: error.response?.status,
      data: errorData,
      message: errorMessage,
      fullError: error
    })
    message.error(`Failed to describe image with OpenAI: ${errorMessage}`)
  } finally {
    isProcessing.value = false
    processingImage.value = null
  }
}

const detectObjectsForImage = async (image: GalleryImage) => {
  if (!image?.id) {
    message.error('No image identifier available')
    return
  }

  isProcessing.value = true
  processingImage.value = image

  try {
    // detect-all runs objects + faces and saves results to the database
    const result = await apiClient.detectAll(image.url || image.id, image.id)

    if (!imageResults.value[image.id]) {
      imageResults.value[image.id] = {}
    }
    // Map response shape to what getModalObjects/getModalPeople expect
    imageResults.value[image.id].yoloDetection = {
      success: result.success,
      objects: result.objects as any,
      faces: result.people as any, // API returns "people", display layer expects "faces"
      processingTime: result.processingTime,
      model: '',
      device: '',
    }

    const objectCount = result.objects?.total ?? 0
    const faceCount = result.people?.total ?? 0
    message.success(`Detected ${objectCount} object(s) and ${faceCount} face(s)`)

    // Refresh so DB-saved results are reflected
    await refreshGallery()
    // Update modalImage so detection sections appear without re-opening the modal
    if (modalImage.value?.id === image.id) {
      const updated = galleryImages.value.find(img => img.id === image.id)
      if (updated) modalImage.value = updated
    }
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || 'Unknown error'
    message.error(`Object detection failed: ${msg}`)
    console.error('Object detection error:', error)
  } finally {
    isProcessing.value = false
    processingImage.value = null
  }
}

const scanTextForImage = async (image: GalleryImage) => {
  if (!image?.id) {
    message.error('No image identifier available')
    return
  }

  isProcessing.value = true
  processingImage.value = image

  try {
    const result = await apiClient.scanText(image.id)
    const count = result.total ?? 0
    message.success(count > 0 ? `Found ${count} text region(s)` : 'No text detected')
    await refreshGallery()
    // Update modalImage so text panel appears without re-opening the modal
    if (modalImage.value?.id === image.id) {
      const updated = galleryImages.value.find(img => img.id === image.id)
      if (updated) modalImage.value = updated
    }
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || 'Unknown error'
    message.error(`Text scan failed: ${msg}`)
    console.error('Text scan error:', error)
  } finally {
    isProcessing.value = false
    processingImage.value = null
  }
}

// Moodboard state
const showMoodboardDialog = ref(false)
const selectedImageForMoodboard = ref<GalleryImage | null>(null)
const availableMoodboards = ref<any[]>([])
const selectedMoodboardId = ref<string | null>(null)
/** Moodboard used by the bookmark button (selected or first). Updated when we load/create moodboards. */
const currentMoodboardIdForBookmark = ref<string | null>(null)

/** Resolve reference asset and moodboards for the current project. Auto-creates reference asset and "mood-a" when missing. */
const getMoodboardContext = async (): Promise<{
  referenceAsset: { id: string } | null
  moodboards: Array<{ id: string; name: string }>
}> => {
  const empty = { referenceAsset: null, moodboards: [] }
  try {
    const savedBrandId = localStorage.getItem('selectedBrandId')
    if (!savedBrandId) return empty
    const savedProjectId = localStorage.getItem(`selectedProject_${savedBrandId}`)
    if (!savedProjectId) return empty

    let referenceAsset = (await apiClient.getAssets({ projectId: savedProjectId, type: 'reference', limit: 10 }))
      .assets.find((a: any) => a.type === 'reference') ?? null
    if (!referenceAsset) {
      const created = await apiClient.createAsset({
        projectId: savedProjectId,
        type: 'reference',
        name: 'Reference'
      })
      referenceAsset = created.asset
    }

    let moodboards = (await apiClient.getMoodboards({ assetId: referenceAsset.id })).moodboards
    if (moodboards.length === 0) {
      const created = await apiClient.createMoodboard({
        assetId: referenceAsset.id,
        name: 'mood-a'
      })
      moodboards = [created.moodboard]
    }
    return { referenceAsset, moodboards }
  } catch (error) {
    console.error('Failed to get moodboard context:', error)
    return empty
  }
}

// Load available moodboards
const loadAvailableMoodboards = async () => {
  const { moodboards } = await getMoodboardContext()
  availableMoodboards.value = moodboards
  if (moodboards.length > 0 && !currentMoodboardIdForBookmark.value) {
    currentMoodboardIdForBookmark.value = moodboards[0].id
  }
}

const moodboardSelectOptions = computed(() =>
  availableMoodboards.value.map((m) => ({ label: m.name, value: m.id }))
)

// Add image to moodboard (dialog submit)
const addImageToMoodboard = async () => {
  if (!selectedImageForMoodboard.value || !selectedMoodboardId.value) {
    return
  }

  try {
    await apiClient.addMoodboardItem(selectedMoodboardId.value, {
      itemType: 'image',
      itemData: JSON.stringify({ imageId: selectedImageForMoodboard.value.id })
    })
    currentMoodboardIdForBookmark.value = selectedMoodboardId.value
    message.success('Image added to moodboard')
    showMoodboardDialog.value = false
    selectedImageForMoodboard.value = null
    selectedMoodboardId.value = null
  } catch (error) {
    console.error('Failed to add image to moodboard:', error)
    message.error('Failed to add image to moodboard')
  }
}

/** Add image to the selected moodboard (or first). Reference asset and "mood-a" are auto-created by getMoodboardContext when missing. */
const addImageToSelectedMoodboard = async (image: GalleryImage) => {
  const { referenceAsset, moodboards } = await getMoodboardContext()
  if (!referenceAsset || moodboards.length === 0) {
    message.warning('Select a project first.')
    return
  }

  const currentId = currentMoodboardIdForBookmark.value
  const validCurrent = currentId && moodboards.some((m) => m.id === currentId)
  const targetMoodboardId = validCurrent ? currentId : moodboards[0].id
  currentMoodboardIdForBookmark.value = targetMoodboardId
  availableMoodboards.value = moodboards

  try {
    await apiClient.addMoodboardItem(targetMoodboardId, {
      itemType: 'image',
      itemData: JSON.stringify({ imageId: image.id })
    })
    message.success('Image added to moodboard')
  } catch (error) {
    console.error('Failed to add image to moodboard:', error)
    message.error('Failed to add image to moodboard')
  }
}

// Open moodboard dialog (getMoodboardContext auto-creates reference asset and "mood-a" when missing)
const openMoodboardDialog = async (image: GalleryImage) => {
  selectedImageForMoodboard.value = image
  await loadAvailableMoodboards()
  if (availableMoodboards.value.length === 0) {
    message.warning('Select a project first.')
    return
  }
  showMoodboardDialog.value = true
}

// Dropdown menu options for processing
const getProcessingOptions = (_image: GalleryImage) => {
  return [
    {
      label: 'Rename',
      key: 'rename'
    },
    {
      label: 'Use as Color Reference',
      key: 'useAsColorReference'
    },
    {
      label: 'Add to Gallery',
      key: 'addToGallery'
    },
    {
      label: 'Add to Moodboard',
      key: 'addToMoodboard'
    },
    {
      type: 'divider',
      key: 'divider-1'
    },
    {
      label: 'Describe 1',
      key: 'describe-1'
    },
    {
      label: 'Describe 2',
      key: 'describe-2'
    },
    {
      label: 'Detect Objects',
      key: 'detect-objects'
    },
    {
      label: 'Scan Text (OCR)',
      key: 'scan-text'
    }
  ]
}

// Gallery selection state for adding images
const showAddToGalleryDialog = ref(false)
const selectedImageForGallery = ref<GalleryImage | null>(null)
const availableGalleries = ref<Array<{ id: string; name: string; accessLevel: string }>>([])
const selectedGalleryForAdd = ref<string[]>([])

// Load available galleries for adding images
const loadAvailableGalleries = async () => {
  try {
    const response = await apiClient.fetchGalleries(selectedBrandId.value)
    availableGalleries.value = response.galleries.filter((g: any) => 
      g.accessLevel === 'WRITE' || g.accessLevel === 'ADMIN'
    )
  } catch (error: any) {
    message.error(`Failed to load galleries: ${error.message || 'Unknown error'}`)
  }
}

// Open add to gallery dialog
const openAddToGalleryDialog = async (image: GalleryImage) => {
  selectedImageForGallery.value = image
  selectedGalleryForAdd.value = image.galleries?.map((g: any) => g.id) || []
  await loadAvailableGalleries()
  if (availableGalleries.value.length === 0) {
    message.warning('No galleries available with write access.')
    return
  }
  showAddToGalleryDialog.value = true
}

// Add image to selected galleries
const addImageToGalleries = async () => {
  if (!selectedImageForGallery.value) return
  if (selectedGalleryForAdd.value.length === 0) {
    message.warning('Please select at least one gallery')
    return
  }

  try {
    for (const galleryId of selectedGalleryForAdd.value) {
      await apiClient.addImagesToGallery(galleryId, [selectedImageForGallery.value.id])
    }
    message.success(`Image added to ${selectedGalleryForAdd.value.length} gallery(ies)`)
    showAddToGalleryDialog.value = false
    selectedImageForGallery.value = null
    selectedGalleryForAdd.value = []
    // Refresh images to show updated gallery badges
    await refreshGallery()
  } catch (error: any) {
    message.error(`Failed to add image to galleries: ${error.message || 'Unknown error'}`)
  }
}

// Handle processing action from dropdown
const handleProcessingAction = async (key: string, image: GalleryImage) => {
  switch (key) {
    case 'rename':
      await renameImage(image)
      break
    case 'useAsColorReference':
      colorSimilarityReferenceImageId.value = image.id
      sortBy.value = 'colorSimilarity'
      message.success(`Using "${image.originalName}" as color reference`)
      await refreshGallery()
      break
    case 'addToGallery':
      await openAddToGalleryDialog(image)
      break
    case 'addToMoodboard':
      await openMoodboardDialog(image)
      break
    case 'describe-1':
      await describeImage(image)
      break
    case 'describe-2':
      await describeImageWithOpenAI(image, 'medium')
      break
    case 'detect-objects':
      await detectObjectsForImage(image)
      break
    case 'scan-text':
      await scanTextForImage(image)
      break
    default:
      console.warn('Unknown processing action:', key)
  }
}

// Handle sort by change
const handleSortByChange = () => {
  if (sortBy.value === 'colorSimilarity' && !colorSimilarityReferenceImageId.value) {
    message.warning('Please select a reference image first (right-click an image → "Use as Color Reference")')
    sortBy.value = 'createdAt'
    return
  }
  // Clear color similarity reference if switching away from color similarity sorting
  if (sortBy.value !== 'colorSimilarity' && colorSimilarityReferenceImageId.value) {
    colorSimilarityReferenceImageId.value = null
  }
  refreshGallery()
}


const copyColorToClipboard = async (colorHex?: string) => {
  if (!colorHex) {
    message.error('No HEX value to copy')
    return
  }
  try {
    await navigator.clipboard.writeText(colorHex)
    // Don't show message here - let addColorToPalette handle the combined message
    return true
  } catch (error) {
    message.error('Failed to copy to clipboard')
    return false
  }
}

// Load color palettes for the current project
const loadColorPalettes = async () => {
  if (!selectedProjectId.value) {
    colorPalettes.value = []
    currentPaletteId.value = ''
    return
  }
  
  try {
    const response = await apiClient.getColorPalettes(selectedProjectId.value)
    colorPalettes.value = response.assets.map((asset: any) => ({
      id: asset.id,
      name: asset.name
    }))
    
    // Load saved current palette from localStorage
    loadCurrentPalette()
  } catch (error) {
    console.error('Failed to load color palettes:', error)
    message.error('Failed to load color palettes')
  }
}

// Load current palette selection from localStorage
const loadCurrentPalette = () => {
  if (!selectedProjectId.value) {
    currentPaletteId.value = ''
    return
  }
  
  const saved = localStorage.getItem(`currentPalette_${selectedProjectId.value}`)
  if (saved && colorPalettes.value.some(p => p.id === saved)) {
    currentPaletteId.value = saved
  } else {
    // Default to first palette (likely 'colors1') if available
    if (colorPalettes.value.length > 0) {
      currentPaletteId.value = colorPalettes.value[0].id
      saveCurrentPalette()
    } else {
      currentPaletteId.value = ''
    }
  }
}

// Save current palette selection to localStorage
const saveCurrentPalette = () => {
  if (selectedProjectId.value && currentPaletteId.value) {
    localStorage.setItem(`currentPalette_${selectedProjectId.value}`, currentPaletteId.value)
  }
}

// Add color to current palette
const addColorToPalette = async (colorData: { hex: string; rgb?: { r: number; g: number; b: number }; hsl?: { h: number; s: number; l: number }; percentage?: number }) => {
  if (!currentPaletteId.value) {
    return false
  }
  
  try {
    await apiClient.addColorToPalette(currentPaletteId.value, colorData)
    return true
  } catch (error: any) {
    console.error('Failed to add color to palette:', error)
    // Don't show error if it's just a duplicate
    if (error?.response?.data?.message?.includes('already exists')) {
      return false // Silently ignore duplicates
    }
    message.error(error?.response?.data?.message || 'Failed to add color to palette')
    return false
  }
}

// Handle color swatch click - copy to clipboard AND add to palette
const handleColorSwatchClick = async (color: { hex?: string; rgb?: { r: number; g: number; b: number }; hsl?: { h: number; s: number; l: number }; percentage?: number }) => {
  const hex = color.hex
  if (!hex) {
    message.error('No HEX value')
    return
  }
  
  // Copy to clipboard (preserve existing behavior)
  const copied = await copyColorToClipboard(hex)
  
  // Add to current palette if selected
  if (currentPaletteId.value) {
    // Create color data with required hex for API
    const colorData: { hex: string; rgb?: { r: number; g: number; b: number }; hsl?: { h: number; s: number; l: number }; percentage?: number } = {
      hex,
      ...(color.rgb && { rgb: color.rgb }),
      ...(color.hsl && { hsl: color.hsl }),
      ...(color.percentage !== undefined && { percentage: color.percentage })
    }
    const added = await addColorToPalette(colorData)
    const paletteName = colorPalettes.value.find(p => p.id === currentPaletteId.value)?.name || 'palette'
    
    if (copied && added) {
      message.success(`Copied ${hex} to clipboard and added to ${paletteName}`)
    } else if (copied) {
      message.success(`Copied ${hex} to clipboard`)
    } else if (added) {
      message.success(`Added ${hex} to ${paletteName}`)
    }
  } else {
    if (copied) {
      message.success(`Copied ${hex} to clipboard (no palette selected)`)
    }
  }
}

// Utility function to format file size
const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Utility function to format date as "11/26/2025, 10:20 AM"
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const year = date.getFullYear()
  const hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const hours12 = hours % 12 || 12
  return `${month}/${day}/${year}, ${hours12}:${minutes} ${ampm}`
}


// Handle image loading errors
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWxsPSIjZjVmNWY1IiBzdHJva2U9IiNkMWQ1ZGIiLz4KPHRleHQgeD0iNjAiIHk9IjYwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTIiPkltYWdlPC90ZXh0Pgo8dGV4dCB4PSI2MCIgeT0iNzUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjNjY2IiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMCI+Tm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K'
}

// Helper function to get the appropriate image URL
const getImageUrl = (image: GalleryImage): string => {
  // Prefer preview URL, fallback to icon URL, then full URL
  return image.previewUrl || image.iconUrl || image.url
}

// Helper functions to get processed data for modal
const getModalDescription = (): string | undefined => {
  if (!modalImage.value) return undefined
  // Check imageResults first (recently processed), then image object
  // Prefer new refImage fields (fullDescription, shortDescription) over legacy description
  return imageResults.value[modalImage.value.id]?.description || 
         imageResults.value[modalImage.value.id]?.fullAnalysis?.description ||
         modalImage.value.fullDescription ||
         modalImage.value.shortDescription ||
         modalImage.value.description // Legacy fallback
}

const getModalColorPalette = (): ColorPaletteResponse | undefined => {
  if (!modalImage.value) return undefined
  // Check imageResults first (recently processed)
  if (imageResults.value[modalImage.value.id]?.colorPalette) {
    return imageResults.value[modalImage.value.id].colorPalette
  }
  
  // Try to build from new refImage fields (dominantColor and palette)
  if (modalImage.value.dominantColor || modalImage.value.palette) {
    try {
      const paletteHexes: string[] = modalImage.value.palette
        ? (typeof modalImage.value.palette === 'string'
            ? JSON.parse(modalImage.value.palette)
            : modalImage.value.palette)
        : []
      
      const dominantHex = modalImage.value.dominantColor || paletteHexes[0]
      
      if (dominantHex) {
        const mappedColors = paletteHexes.map((hex: string) => ({
          hex,
          percentage: undefined,
          rgb: undefined,
          hsl: undefined
        }))
        
        return {
          colors: mappedColors.length > 0 ? mappedColors : [{ hex: dominantHex }],
          dominantColor: { hex: dominantHex },
          palette: mappedColors.length > 0 ? mappedColors : [{ hex: dominantHex }],
          algorithm: 'colorthief',
          processingTime: 0
        } as ColorPaletteResponse
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  // Fallback: Try to parse from legacy dominantColors if available
  if (modalImage.value.dominantColors) {
    try {
      const colors = typeof modalImage.value.dominantColors === 'string'
        ? JSON.parse(modalImage.value.dominantColors)
        : modalImage.value.dominantColors
      if (Array.isArray(colors) && colors.length > 0) {
        const mappedColors = colors.map((c: any) => ({
          hex: c.hex || c,
          percentage: c.weight || c.percentage,
          rgb: c.rgb,
          hsl: c.hsl
        }))
        return {
          colors: mappedColors,
          dominantColor: colors[0]?.hex ? {
            hex: colors[0].hex,
            rgb: colors[0].rgb,
            hsl: colors[0].hsl
          } : mappedColors[0],
          palette: mappedColors,
          algorithm: 'database',
          processingTime: 0
        } as ColorPaletteResponse
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  return undefined
}



// Helper function to get fullDescription from database or processed results
const getModalFullDescription = (): string | undefined => {
  if (!modalImage.value) return undefined
  // Check imageResults first (recently processed)
  const ollamaStructured = imageResults.value[modalImage.value.id]?.ollamaStructured
  if (ollamaStructured?.fullDescription) {
    return ollamaStructured.fullDescription
  }
  // Check database field
  if (modalImage.value.fullDescription) {
    return modalImage.value.fullDescription
  }
  // Fallback to other description fields
  return getModalDescription()
}

// Helper function to get faces detection data
const getModalPeople = (): Array<{ name: string; face: boolean; confidence: number; bbox: number[] }> => {
  if (!modalImage.value) return []
  
  // Check imageResults first (recently processed YOLO results)
  const yoloDetection = imageResults.value[modalImage.value.id]?.yoloDetection
  console.log('getModalPeople - checking for faces:', {
    imageId: modalImage.value.id,
    hasYoloDetection: !!yoloDetection,
    hasFaces: !!yoloDetection?.faces,
    facesData: yoloDetection?.faces
  })
  
  if (yoloDetection?.faces) {
    const faces = yoloDetection.faces.detections || []
    console.log('getModalPeople - found faces in yoloDetection:', faces.length)
    return faces.map((face: any, index: number) => ({
      name: face.name || `Person ${index + 1}`,
      face: true,
      confidence: face.confidence || 0,
      bbox: face.bbox || face.rect || []
    }))
  }
  
  // Check database field (faces)
  try {
    if (modalImage.value.faces) {
      const faces = typeof modalImage.value.faces === 'string'
        ? JSON.parse(modalImage.value.faces)
        : modalImage.value.faces
      if (Array.isArray(faces)) {
        return faces.map((face: any) => ({
          name: face.name || 'Person',
          face: true,
          confidence: face.confidence || 0,
          bbox: face.rect || face.bbox || []
        }))
      }
    }
    // Fallback to legacy people field
    if (modalImage.value.people) {
      const people = typeof modalImage.value.people === 'string'
        ? JSON.parse(modalImage.value.people)
        : modalImage.value.people
      if (Array.isArray(people)) {
        return people.map((person: any) => ({
          name: person.name || 'Person',
          face: true,
          confidence: person.confidence || 0,
          bbox: person.rect || person.bbox || []
        }))
      }
    }
  } catch (e) {
    console.error('Failed to parse faces:', e)
  }
  return []
}

// Helper function to get objects detection data
const getModalObjects = (): Array<{ name: string; bbox: number[]; confidence?: number }> => {
  if (!modalImage.value) return []
  
  // Check imageResults first (recently processed YOLO results)
  const yoloDetection = imageResults.value[modalImage.value.id]?.yoloDetection
  console.log('getModalObjects - checking for objects:', {
    imageId: modalImage.value.id,
    hasYoloDetection: !!yoloDetection,
    hasObjects: !!yoloDetection?.objects,
    objectsData: yoloDetection?.objects
  })
  
  if (yoloDetection?.objects) {
    const objects = yoloDetection.objects.detections || []
    console.log('getModalObjects - found objects in yoloDetection:', objects.length)
    return objects
      .filter((obj: any) => {
        const name = (obj.name || 'object').toLowerCase().trim()
        // Filter out 'person' objects since faces are shown separately
        return name !== 'person'
      })
      .map((obj: any) => ({
        name: obj.name || 'object',
        bbox: obj.bbox || obj.rect || [],
        confidence: obj.confidence || 0
      }))
  }
  
  // Check database field
  try {
    if (modalImage.value.objects) {
      const objects = typeof modalImage.value.objects === 'string'
        ? JSON.parse(modalImage.value.objects)
        : modalImage.value.objects
      if (Array.isArray(objects)) {
        return objects
          .filter((obj: any) => {
            const name = (obj.name || 'object').toLowerCase().trim()
            // Filter out 'person' objects since faces are shown separately
            return name !== 'person'
          })
          .map((obj: any) => ({
            name: obj.name || 'object',
            bbox: obj.rect || obj.bbox || [],
            confidence: obj.confidence || 0
          }))
      }
    }
  } catch (e) {
    console.error('Failed to parse objects:', e)
  }
  return []
}

const getModalText = (): Array<{ text: string; bbox: number[]; confidence: number }> => {
  if (!modalImage.value) return []
  try {
    if (modalImage.value.text) {
      const blocks = typeof modalImage.value.text === 'string'
        ? JSON.parse(modalImage.value.text)
        : modalImage.value.text
      if (Array.isArray(blocks)) {
        return blocks
          .filter((b: any) => b.text && Array.isArray(b.bbox) && b.bbox.length === 4)
          .map((b: any) => ({
            text: b.text,
            bbox: b.bbox,
            confidence: b.confidence ?? 0
          }))
      }
    }
  } catch (e) {
    console.error('Failed to parse text blocks:', e)
  }
  return []
}

// Predefined array of 20 distinct colors for tags and rectangles
const DETECTION_COLORS = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#FFA07A', // Light Salmon
  '#98D8C8', // Mint
  '#F7DC6F', // Yellow
  '#BB8FCE', // Purple
  '#85C1E2', // Sky Blue
  '#F8B739', // Orange
  '#52BE80', // Green
  '#EC7063', // Coral
  '#5DADE2', // Light Blue
  '#F1948A', // Pink
  '#82E0AA', // Light Green
  '#F4D03F', // Gold
  '#AF7AC5', // Lavender
  '#48C9B0', // Turquoise
  '#F5B041', // Amber
  '#E74C3C', // Bright Red
  '#3498DB'  // Bright Blue
]

// Helper function to get a color from the predefined array (cycles through)
const getTagColor = (index: number): string => {
  return DETECTION_COLORS[index % DETECTION_COLORS.length]
}

// Per-item toggle helpers
const toggleFaceIndex = (i: number) => {
  const arr = [...visibleFaceIndices.value]
  const pos = arr.indexOf(i)
  if (pos > -1) arr.splice(pos, 1)
  else arr.push(i)
  visibleFaceIndices.value = arr
}

const toggleObjectIndex = (i: number) => {
  const arr = [...visibleObjectIndices.value]
  const pos = arr.indexOf(i)
  if (pos > -1) arr.splice(pos, 1)
  else arr.push(i)
  visibleObjectIndices.value = arr
}

const toggleTextIndex = (i: number) => {
  const arr = [...visibleTextIndices.value]
  const pos = arr.indexOf(i)
  if (pos > -1) arr.splice(pos, 1)
  else arr.push(i)
  visibleTextIndices.value = arr
}

// Show / hide all helpers
const showAllFaces = () => { visibleFaceIndices.value = getModalPeople().map((_, i) => i) }
const hideAllFaces = () => { visibleFaceIndices.value = [] }
const showAllObjects = () => { visibleObjectIndices.value = getModalObjects().map((_, i) => i) }
const hideAllObjects = () => { visibleObjectIndices.value = [] }
const showAllText = () => { visibleTextIndices.value = getModalText().map((_, i) => i) }
const hideAllText = () => { visibleTextIndices.value = [] }

// Get face rectangle style for positioning
const getFaceRectangleStyle = (person: { name: string; bbox: number[] }, index: number): Record<string, string> => {
  if (!imageModalRef.value || !person.bbox || person.bbox.length !== 4) {
    return { display: 'none' }
  }
  
  const container = imageModalRef.value
  const img = container.querySelector('img') as HTMLImageElement
  if (!img || !img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
    return { display: 'none' }
  }
  
  // Get actual displayed image dimensions
  const imgRect = img.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  const imgNaturalWidth = img.naturalWidth
  const imgNaturalHeight = img.naturalHeight
  
  // Calculate scale factors (image might be scaled to fit container)
  const scaleX = imgRect.width / imgNaturalWidth
  const scaleY = imgRect.height / imgNaturalHeight
  
  // Normalized coordinates [x1, y1, x2, y2] in 0-1 range
  const [x1, y1, x2, y2] = person.bbox
  
  // Calculate pixel positions relative to the container
  // Account for image centering within container
  const imgOffsetX = (imgRect.left - containerRect.left)
  const imgOffsetY = (imgRect.top - containerRect.top)
  
  const left = imgOffsetX + (x1 * imgNaturalWidth * scaleX)
  const top = imgOffsetY + (y1 * imgNaturalHeight * scaleY)
  const width = (x2 - x1) * imgNaturalWidth * scaleX
  const height = (y2 - y1) * imgNaturalHeight * scaleY
  
  const color = getTagColor(index)
  
  return {
    position: 'absolute',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    border: `2px solid ${color}`,
    pointerEvents: 'none',
    boxSizing: 'border-box'
  }
}

// Get object rectangle style for positioning
const getObjectRectangleStyle = (obj: { name: string; bbox: number[] }, index: number): Record<string, string> => {
  if (!imageModalRef.value || !obj.bbox || obj.bbox.length !== 4) {
    return { display: 'none' }
  }
  
  const container = imageModalRef.value
  const img = container.querySelector('img') as HTMLImageElement
  if (!img || !img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
    return { display: 'none' }
  }
  
  // Get actual displayed image dimensions
  const imgRect = img.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  const imgNaturalWidth = img.naturalWidth
  const imgNaturalHeight = img.naturalHeight
  
  // Calculate scale factors (image might be scaled to fit container)
  const scaleX = imgRect.width / imgNaturalWidth
  const scaleY = imgRect.height / imgNaturalHeight
  
  // Normalized coordinates [x1, y1, x2, y2] in 0-1 range
  const [x1, y1, x2, y2] = obj.bbox
  
  // Calculate pixel positions relative to the container
  // Account for image centering within container
  const imgOffsetX = (imgRect.left - containerRect.left)
  const imgOffsetY = (imgRect.top - containerRect.top)
  
  const left = imgOffsetX + (x1 * imgNaturalWidth * scaleX)
  const top = imgOffsetY + (y1 * imgNaturalHeight * scaleY)
  const width = (x2 - x1) * imgNaturalWidth * scaleX
  const height = (y2 - y1) * imgNaturalHeight * scaleY
  
  const color = getTagColor(index)
  
  return {
    position: 'absolute',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    border: `2px solid ${color}`,
    pointerEvents: 'none',
    boxSizing: 'border-box'
  }
}

// Force rectangle repositioning after image loads/resizes
const updateFaceRectangles = () => {
  if (visibleFaceIndices.value.length > 0) {
    const was = [...visibleFaceIndices.value]
    visibleFaceIndices.value = []
    nextTick(() => { visibleFaceIndices.value = was })
  }
}

const updateObjectRectangles = () => {
  if (visibleObjectIndices.value.length > 0) {
    const was = [...visibleObjectIndices.value]
    visibleObjectIndices.value = []
    nextTick(() => { visibleObjectIndices.value = was })
  }
}

const updateTextRectangles = () => {
  if (visibleTextIndices.value.length > 0) {
    const was = [...visibleTextIndices.value]
    visibleTextIndices.value = []
    nextTick(() => { visibleTextIndices.value = was })
  }
}

// Get text rectangle style for positioning
const getTextRectangleStyle = (block: { text: string; bbox: number[] }, index: number): Record<string, string> => {
  if (!imageModalRef.value || !block.bbox || block.bbox.length !== 4) {
    return { display: 'none' }
  }

  const container = imageModalRef.value
  const img = container.querySelector('img') as HTMLImageElement
  if (!img || !img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
    return { display: 'none' }
  }

  const imgRect = img.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  const imgNaturalWidth = img.naturalWidth
  const imgNaturalHeight = img.naturalHeight

  const scaleX = imgRect.width / imgNaturalWidth
  const scaleY = imgRect.height / imgNaturalHeight

  const [x1, y1, x2, y2] = block.bbox

  const imgOffsetX = imgRect.left - containerRect.left
  const imgOffsetY = imgRect.top - containerRect.top

  const left = imgOffsetX + (x1 * imgNaturalWidth * scaleX)
  const top = imgOffsetY + (y1 * imgNaturalHeight * scaleY)
  const width = (x2 - x1) * imgNaturalWidth * scaleX
  const height = (y2 - y1) * imgNaturalHeight * scaleY

  const color = getTagColor(index)

  return {
    position: 'absolute',
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`,
    border: `2px solid ${color}`,
    background: `${color}1a`,
    pointerEvents: 'none',
    boxSizing: 'border-box'
  }
}


// Helper function to get keywords from database or processed results
const getModalKeywords = (): string[] => {
  if (!modalImage.value) return []
  
  // Check if keywords is already an array (from API parsing)
  if (Array.isArray(modalImage.value.keywords)) {
    return modalImage.value.keywords
  }
  
  // Check imageResults first (recently processed)
  const ollamaStructured = imageResults.value[modalImage.value.id]?.ollamaStructured
  if (ollamaStructured?.keywords && ollamaStructured.keywords.length > 0) {
    return ollamaStructured.keywords
  }
  // Check database field (stored as JSON string)
  if (modalImage.value.keywords) {
    try {
      const keywords = typeof modalImage.value.keywords === 'string'
        ? JSON.parse(modalImage.value.keywords)
        : modalImage.value.keywords
      return Array.isArray(keywords) ? keywords : []
    } catch (e) {
      console.error('Failed to parse keywords:', e)
      return []
    }
  }
  return []
}

/** Remove a keyword from the current modal image; updates DB and local state. */
const removeKeyword = async (keywordToRemove: string) => {
  if (!modalImage.value?.id) return
  const current = getModalKeywords()
  const next = current.filter(k => k !== keywordToRemove)
  if (next.length === current.length) return
  try {
    const { keywords } = await apiClient.updateImageKeywords(modalImage.value.id, next)
    const keywordsJson = JSON.stringify(keywords)
    const imageId = modalImage.value.id
    const imageIndex = galleryImages.value.findIndex(img => img.id === imageId)
    if (imageIndex > -1) {
      galleryImages.value[imageIndex].keywords = keywordsJson
    }
    if (modalImage.value) {
      modalImage.value.keywords = keywordsJson
    }
    if (selectedImage.value?.id === imageId) {
      selectedImage.value.keywords = keywordsJson
    }
    if (imageResults.value[modalImage.value.id]?.ollamaStructured) {
      imageResults.value[modalImage.value.id].ollamaStructured!.keywords = keywords
    }
    message.success(`Removed keyword "${keywordToRemove}"`)
  } catch (e: any) {
    message.error(e?.response?.data?.message || e?.message || 'Failed to remove keyword')
  }
}

</script>

<style scoped>
.gallery-tab {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
  background: var(--base-color);
}

.gallery-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0 16px;
  min-height: 0;
}

.main-content {
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

@media (max-width: 1024px) {
  .main-content {
    flex-direction: column;
    height: auto;
  }
}

.gallery-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
  padding: 8px 12px;
  background: var(--card-color);
  border-radius: 6px;
  border: 1px solid var(--border-color);
  flex-shrink: 0;
}

.gallery-icon {
  display: none;
}

.gallery-info {
  flex: 1;
}

.gallery-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-color);
}

.gallery-subtitle {
  display: none;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.upload-button,
.refresh-button,
.clear-db-button {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  color: var(--text-color-2);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-button:hover,
.refresh-button:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(24, 160, 88, 0.1);
}

.restore-trash-button {
  border-color: #1890ff;
  color: #1890ff;
}

.restore-trash-button:hover {
  background: rgba(24, 144, 255, 0.1);
  border-color: #1890ff;
  color: #1890ff;
}

.empty-trash-button {
  border-color: #ff4d4f;
  color: #ff4d4f;
}

.empty-trash-button:hover {
  background: rgba(255, 77, 79, 0.1);
  border-color: #ff4d4f;
  color: #ff4d4f;
}

.clear-db-button {
  border-color: #ef4444;
  color: #ef4444;
}

.clear-db-button:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #dc2626;
  color: #dc2626;
}

.upload-button.active {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(24, 160, 88, 0.1);
}

.gallery-images {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
  min-height: 0;
}

@media (max-width: 1024px) {
  .gallery-images {
    padding-right: 0;
    margin-bottom: 24px;
  }
}

.empty-gallery {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  padding: 16px 0;
}

.image-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.image-item:hover {
  transform: translateY(-2px);
}

.image-card {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  background: var(--card-color);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.image-card:hover {
  border-color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(24, 160, 88, 0.2);
}

.image-item.selected .image-card {
  border-color: var(--primary-color);
  border-width: 3px;
  box-shadow: 0 0 0 2px rgba(24, 160, 88, 0.2);
}

.image-item.color-reference .image-card {
  border-color: #ff9800;
  border-width: 3px;
  box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.3);
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.image-card:hover .image-overlay {
  opacity: 1;
}

.view-button,
.bookmark-button,
.delete-button {
  background: rgba(128, 128, 128, 0.9);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
}

.view-button:hover {
  background: var(--primary-color);
  color: white;
  transform: scale(1.1);
}

.bookmark-button:hover {
  background: #eab308;
  color: white;
  transform: scale(1.1);
}

.delete-button:hover {
  background: #ef4444;
  color: white;
  transform: scale(1.1);
}

.image-name {
  margin: 0;
  font-size: 12px;
  color: var(--text-color-2);
  text-align: center;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  word-break: break-all;
  line-height: 1.3;
  max-width: 100%;
  overflow-wrap: break-word;
  white-space: normal;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.trash-icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

/* Processing Buttons */
.processing-buttons {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  justify-content: center;
}

.process-dropdown-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
}

.process-dropdown-button:hover:not(:disabled) {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(24, 160, 88, 0.1);
}

.process-dropdown-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-process-button {
  width: 100%;
  justify-content: center;
  padding: 8px 12px;
  margin-top: 8px;
}

.process-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  color: var(--text-color-2);
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.process-button:hover:not(:disabled) {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(24, 160, 88, 0.1);
}

.process-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.palette-button:hover:not(:disabled) {
  border-color: #52c41a;
  color: #52c41a;
  background: rgba(82, 196, 26, 0.1);
}

.yolo-button:hover:not(:disabled) {
  border-color: #ff6b6b;
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.1);
}

.blip-button:hover:not(:disabled) {
  border-color: #9c27b0;
  color: #9c27b0;
  background: rgba(156, 39, 176, 0.1);
}

/* Upload Pane Styles */
.upload-pane {
  width: 500px;
  height: 100%;
  background: var(--card-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

@media (max-width: 1024px) {
  .upload-pane {
    width: 100%;
    height: 60vh;
  }
}

.upload-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--base-color);
}

.upload-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-color);
}

.close-button {
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-color-2);
  transition: all 0.2s ease;
}

.close-button:hover {
  background: var(--hover-color);
  color: var(--text-color);
}

.upload-content {
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.upload-progress-block {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--base-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.upload-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-color);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: upload-spin 0.8s linear infinite;
}

@keyframes upload-spin {
  to {
    transform: rotate(360deg);
  }
}

.upload-progress-text {
  margin: 0;
  font-size: 14px;
  color: var(--text-color-2);
}

.upload-dropzone-busy {
  opacity: 0.7;
  pointer-events: none;
}

.upload-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.option-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.option-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-color);
}

.option-input {
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--base-color);
  color: var(--text-color);
  font-size: 14px;
}

.option-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(24, 160, 88, 0.2);
}


.upload-dropzone {
  flex: 1;
  min-height: 200px;
  border: 2px dashed var(--border-color);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s;
  cursor: default;
}

.upload-dropzone.drag-over {
  border-color: var(--primary-color);
  background: rgba(24, 160, 88, 0.06);
}

.drop-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
}

.drop-icon {
  color: var(--text-color-3);
  opacity: 0.6;
}

.drop-text {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-color);
}

.drop-subtext {
  margin: 0;
  font-size: 12px;
  color: var(--text-color-3);
}

.drop-buttons {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.browse-btn {
  padding: 6px 14px;
  background: var(--card-color);
  border: 1px solid var(--border-color);
  border-radius: 5px;
  color: var(--text-color);
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.browse-btn:hover:not(:disabled) {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.browse-btn.folder-btn {
  background: rgba(24, 160, 88, 0.08);
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.browse-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.upload-actions {
  display: flex;
  gap: 12px;
  margin-top: auto;
}

.upload-btn-primary {
  flex: 1;
  padding: 10px 16px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-btn-primary:hover {
  background: #16a34a;
}

.upload-btn-secondary {
  padding: 10px 16px;
  background: transparent;
  color: var(--text-color-2);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-btn-secondary:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.description-text {
  margin: 0;
  line-height: 1.5;
  color: var(--text-color-2);
  font-size: 13px;
}

.color-palette-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  align-items: stretch;
}

.palette-spacer {
  width: 16px;
  height: 1px;
  background: var(--border-color);
  margin: 0 4px;
}

.color-swatch {
  position: relative;
  width: 100%;
  min-width: 60px;
  height: 40px;
  border-radius: 0;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  flex-shrink: 0;
}

.color-swatch:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: var(--primary-color);
}

.color-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 4px;
  font-size: 10px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  text-align: center;
  backdrop-filter: blur(4px);
}

/* Gallery Controls */
.gallery-selector-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gallery-selector-group .control-label {
  font-weight: 500;
  white-space: nowrap;
}

.gallery-selector-component {
  min-width: 300px;
  flex: 1;
}

.moodboard-selector-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.moodboard-selector-label {
  font-size: 12px;
  color: var(--text-color-2);
  white-space: nowrap;
}

.moodboard-select {
  min-width: 160px;
}

.image-galleries {
  display: flex;
  flex-wrap: wrap;
  margin-top: 8px;
  margin-bottom: 4px;
}

.add-to-gallery-dialog {
  padding: 8px 0;
}

.gallery-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  background: var(--card-color);
  border-radius: 6px;
  border: 1px solid var(--border-color);
  margin-bottom: 6px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.control-label {
  font-size: 12px;
  color: var(--text-color-2);
  white-space: nowrap;
}

.palette-select {
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--base-color);
  color: var(--text-color);
  font-size: 14px;
  min-width: 150px;
  cursor: pointer;
}

.palette-select:focus {
  outline: none;
  border-color: var(--primary-color);
}

.palette-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-input {
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--base-color);
  color: var(--text-color);
  font-size: 14px;
  min-width: 250px;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.search-button {
  padding: 8px 16px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.search-button:hover {
  background: #16a34a;
}

.sort-select {
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--base-color);
  color: var(--text-color);
  font-size: 14px;
  cursor: pointer;
}

.sort-order-button {
  padding: 8px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-color-2);
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.sort-order-button:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.results-count {
  font-size: 13px;
  color: var(--text-color-2);
  white-space: nowrap;
}

.filter-tags-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: var(--primary-color-suppl, rgba(99,226,183,0.12));
  border: 1px solid var(--primary-color, #63e2b7);
  border-radius: 12px;
  font-size: 12px;
  color: var(--text-color-1);
}

.filter-tag-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-color-2);
  font-size: 14px;
  line-height: 1;
  padding: 0;
}

.filter-tag-remove:hover {
  color: var(--error-color);
}

.controls-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

.export-parts {
  display: flex;
  align-items: center;
  gap: 8px;
}
.export-parts-label {
  font-size: 13px;
  color: var(--text-color-2);
}
.export-part-button {
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--base-color);
  color: var(--text-color);
  cursor: pointer;
}
.export-part-button:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.import-progress {
  font-size: 13px;
  color: var(--text-color-2);
}
.import-progress-label {
  margin-right: 8px;
}
.import-progress-count {
  font-weight: 500;
}

.color-similarity-indicator {
  color: #ff9800;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.clear-filter-button {
  background: transparent;
  border: none;
  color: #ff9800;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  transition: background 0.2s;
}

.clear-filter-button:hover {
  background: rgba(255, 152, 0, 0.1);
}

.quick-scan-button {
  padding: 8px 16px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.quick-scan-button:hover:not(:disabled) {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(24, 160, 88, 0.1);
}

.quick-scan-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.full-scan-button {
  margin-left: 8px;
}

.view-queue-button {
  margin-left: 8px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-color-secondary, #888);
  font-size: 13px;
  cursor: pointer;
}
.view-queue-button:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

/* Queue modal */
.queue-modal-body {
  min-height: 120px;
}
.queue-empty,
.queue-loading {
  color: var(--text-color-secondary);
  margin: 0;
}
.queue-job-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;
}
.queue-job-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);
}
.queue-job-item:last-child {
  border-bottom: none;
}
.queue-job-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}
.queue-job-thumb-placeholder {
  background: var(--border-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color-secondary);
  font-size: 14px;
}
.queue-job-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}
.queue-job-type {
  font-size: 12px;
  color: var(--text-color-secondary);
}
.queue-job-status {
  font-size: 12px;
  font-weight: 500;
}
.queue-job-status.processing {
  color: var(--primary-color);
}
.queue-job-status.pending {
  color: var(--text-color-secondary);
}
.queue-modal-hint {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
  font-size: 12px;
  color: var(--text-color-secondary);
}

/* Infinite scroll sentinel */
.scroll-sentinel {
  display: flex;
  justify-content: center;
  padding: 16px;
  min-height: 40px;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.gallery-images {
  position: relative;
}

/* Image Modal */
/* ===== Type Library Styles ===== */
.type-library-filters {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(0,0,0,0.2);
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.category-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.category-pill {
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 20px;
  padding: 3px 12px;
  font-size: 12px;
  color: #aaa;
  cursor: pointer;
  transition: all 0.15s;
  text-transform: capitalize;
}

.category-pill:hover,
.category-pill.active {
  background: var(--primary-color, #6366f1);
  border-color: var(--primary-color, #6366f1);
  color: white;
}

.font-item .image-card {
  position: relative;
}

.font-placeholder {
  width: 100%;
  aspect-ratio: 16/9;
  background: #1a1a2e;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.font-placeholder-name {
  font-size: 24px;
  color: #e2e8f0;
  text-align: center;
  word-break: break-word;
}

.font-card-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.75));
  padding: 24px 10px 8px;
  pointer-events: none;
}

.font-card-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: white;
}

.font-card-category {
  display: block;
  font-size: 11px;
  color: rgba(255,255,255,0.6);
  text-transform: capitalize;
  margin-top: 2px;
}

.font-family-count {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0,0,0,0.6);
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  pointer-events: none;
  backdrop-filter: blur(4px);
}

.font-family-style-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 260px;
  overflow-y: auto;
}

.font-family-style-item {
  width: 100%;
  text-align: left;
  padding: 7px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(255,255,255,0.75);
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
}

.font-family-style-item:hover {
  background: rgba(255,255,255,0.08);
  color: white;
}

.font-family-style-item.active {
  background: rgba(99,102,241,0.25);
  color: white;
  font-weight: 600;
}

/* Font modal specific */
.font-modal {
  max-width: 1280px; /* room for 1920×1080 landscape specimen */
}

.font-modal .image-modal-image-section {
  align-items: flex-start;
  justify-content: flex-start;
}

.font-modal-gallery {
  position: relative;
  background: #f0f0ec;
  min-height: 200px;
  width: 100%;
}

.font-modal-images-scroll {
  height: min(78vh, 960px);
  overflow: hidden;
  padding: 8px 12px 20px;
  display: grid;
  grid-template-rows: 1fr 1fr;
  gap: 16px;
}

.font-modal-top-split {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) minmax(280px, 1.15fr);
  gap: 16px;
  align-items: start;
  overflow: hidden;
}

.font-modal-top-col {
  min-width: 0;
}

.font-modal-top-col--samples {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: min(36vh, 420px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.font-modal-figure--square .font-specimen-img {
  max-height: min(36vh, 420px);
  width: 100%;
  object-fit: contain;
  display: block;
}

.font-modal-figure--sample .font-specimen-img {
  max-height: min(30vh, 360px);
  width: 100%;
}

.font-modal-figure--landscape .font-specimen-img {
  max-height: min(36vh, 420px);
  width: 100%;
  object-fit: contain;
  display: block;
}

.font-modal-pane-empty {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  text-align: center;
  font-size: 13px;
  color: #888;
  background: #fafaf6;
  border: 1px dashed #d4d4cc;
  border-radius: 10px;
  line-height: 1.4;
}

@media (max-width: 900px) {
  .font-modal-top-split {
    grid-template-columns: 1fr;
  }

  .font-modal-top-col--samples {
    max-height: none;
  }
}

.font-modal-figure {
  margin: 0;
  background: #fff;
  border: 1px solid var(--border-color, #e2e2dc);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.font-modal-figure-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #6b6b6b;
  padding: 10px 14px;
  border-bottom: 1px solid #ecece8;
  background: #fafaf8;
}

.font-modal-figure .font-specimen-img {
  width: 100%;
  height: auto;
  vertical-align: middle;
  display: block;
}

.font-specimen-img {
  object-fit: contain;
  background: white;
}

.font-no-specimen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #888;
  font-size: 14px;
}

.font-modal-name {
  font-size: 28px;
  font-weight: 400;
  margin: 0 0 8px;
  line-height: 1.2;
  color: #f0f0f0;
}

.font-badges {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.font-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.category-badge {
  background: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
  border: 1px solid rgba(99, 102, 241, 0.3);
  text-transform: capitalize;
}

.whitelisted-badge {
  background: rgba(34, 197, 94, 0.15);
  color: #86efac;
  border: 1px solid rgba(34, 197, 94, 0.25);
}

.font-weights-list,
.font-tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.font-weight-pill {
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 11px;
  color: #ccc;
}

.font-tag-pill {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  color: #aaa;
}

.font-action-btn {
  display: block;
  width: 100%;
  padding: 8px 16px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 6px;
  color: #e2e8f0;
  font-size: 13px;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  transition: background 0.15s;
}

.font-action-btn:hover {
  background: rgba(255,255,255,0.15);
}

.font-action-btn--primary {
  background: rgba(99, 179, 237, 0.2);
  border-color: rgba(99, 179, 237, 0.4);
  color: #90cdf4;
}

.font-action-btn--primary:hover {
  background: rgba(99, 179, 237, 0.35);
}

.font-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.font-source-link {
  color: #90cdf4;
  word-break: break-all;
  font-size: 12px;
}

.font-description {
  font-size: 12px;
  color: #a0aec0;
  line-height: 1.5;
  margin-top: 4px;
}

.font-describe-result {
  border: 1px solid rgba(99, 179, 237, 0.25);
  border-radius: 6px;
  padding: 10px;
  background: rgba(99, 179, 237, 0.05);
}

/* ===== End Type Library Styles ===== */

.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.image-modal {
  background: var(--card-color);
  border-radius: 12px;
  max-width: 95vw;
  max-height: 95vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.image-modal-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--base-color);
}

.image-modal-close {
  background: transparent;
  border: none;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-color-2);
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.image-modal-close:hover {
  background: var(--hover-color);
  color: var(--text-color);
}

.image-modal-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: calc(95vh - 80px);
  height: 100%;
}

.image-modal-body {
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: hidden;
  height: 100%;
}

.image-modal-image-section {
  width: 75%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--base-color);
  padding: 20px;
  overflow: auto;
}

.image-modal-data-section {
  width: 25%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--border-color);
  background: var(--card-color);
  overflow-y: auto;
  padding: 20px;
}

@media (max-width: 1024px) {
  .image-modal-body {
    flex-direction: column;
  }
  
  .image-modal-image-section {
    width: 100%;
    height: 60vh;
  }
  
  .image-modal-data-section {
    width: 100%;
    height: auto;
    border-left: none;
    border-top: 1px solid var(--border-color);
    max-height: 40vh;
  }
}

.image-modal-image-container {
  position: relative;
  display: inline-block;
  max-width: 100%;
  max-height: 100%;
}

.image-modal-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  background: var(--base-color);
  display: block;
}

.detection-rectangle {
  position: absolute;
  border: 2px solid;
  pointer-events: none;
  box-sizing: border-box;
  z-index: 10;
}

.detection-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.detection-section-header .image-modal-section-title {
  margin: 0;
}

.detection-controls {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.detection-control-btn {
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background: transparent;
  color: var(--text-color-2);
  cursor: pointer;
  line-height: 1.6;
}

.detection-control-btn:hover {
  background: var(--hover-color);
  color: var(--text-color);
}

.text-blocks-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.text-block-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 4px 6px;
  border-left: 3px solid;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 2px;
  cursor: pointer;
  font-size: 12px;
}

.text-block-item:hover {
  background: rgba(255, 255, 255, 0.08);
}

.text-block-content {
  flex: 1;
  word-break: break-word;
  line-height: 1.4;
}

.text-block-confidence {
  flex-shrink: 0;
  margin-left: 8px;
  opacity: 0.5;
  font-size: 10px;
}

.image-modal-info {
  padding: 20px;
  border-top: 1px solid var(--border-color);
  background: var(--base-color);
  max-height: 400px;
  overflow-y: auto;
}

.image-modal-section {
  margin-bottom: 24px;
}

.image-modal-section:last-child {
  margin-bottom: 0;
}

.image-modal-section-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.image-modal-metadata {
  margin-bottom: 0;
}

.image-modal-metadata p {
  margin: 6px 0;
  font-size: 13px;
  color: var(--text-color-2);
  line-height: 1.4;
}

.metadata-line {
  margin: 4px 0;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}

.metadata-link {
  color: var(--primary-color);
  text-decoration: none;
  word-break: break-all;
}

.metadata-link:hover {
  text-decoration: underline;
}

.metadata-subsection {
  margin: 12px 0;
}

.metadata-list {
  margin: 6px 0 0 0;
  padding-left: 20px;
  list-style-type: disc;
}

.metadata-list li {
  margin: 4px 0;
  font-size: 13px;
  color: var(--text-color);
}

.metadata-subtext {
  color: var(--text-color-2);
  font-size: 12px;
  font-style: italic;
}

.metadata-line {
  margin: 8px 0;
  font-size: 14px;
  color: var(--text-color);
  line-height: 1.5;
}

.metadata-separator {
  margin: 0 8px;
  color: var(--text-color-2);
}

.description-line {
  color: var(--text-color-2);
  font-style: italic;
}

.image-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
}

.image-modal-action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--card-color);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-color);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.image-modal-action-button:first-child {
  border-color: #ef4444;
  color: #ef4444;
}

.image-modal-action-button:first-child:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #dc2626;
  color: #dc2626;
}

.image-modal-action-button:not(:first-child):hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(24, 160, 88, 0.1);
}

.image-modal-action-button svg {
  flex-shrink: 0;
}

/* Modal Processed Data Styles */
.image-modal-processed-data {
  margin-top: 20px;
  border-top: 1px solid var(--border-color);
  padding-top: 20px;
}

.modal-result-section {
  margin-bottom: 20px;
}

.modal-result-section h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color);
}

.modal-result-section h5 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
}

.modal-result-content {
  background: var(--card-color);
  border-radius: 6px;
  border: 1px solid var(--border-color);
  padding: 16px;
}

.modal-result-content p {
  margin: 0;
  line-height: 1.5;
  color: var(--text-color);
}

.modal-result-content h5 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
}

.keywords-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.detection-tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.keyword-tag {
  display: inline-block;
  padding: 4px 8px;
  background: var(--base-color);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 11px;
  color: var(--text-color);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  margin: 2px;
}

.text-lines {
  margin: 8px 0 0 0;
  padding-left: 20px;
  list-style-type: disc;
}

.text-lines li {
  margin: 4px 0;
  font-size: 13px;
  color: var(--text-color);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Upload Progress */
.upload-progress {
  margin: 16px 24px;
  padding: 12px 16px;
  background: var(--card-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--text-color);
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: var(--base-color);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--primary-color);
  transition: width 0.3s ease;
}

.quick-scan-progress {
  margin-top: 8px;
  width: 240px;
}

.quick-scan-progress .progress-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: var(--text-color);
  margin-bottom: 6px;
}

.quick-scan-progress .progress-failed {
  color: #ef4444;
  font-weight: 600;
}

/* Resume Dialog */
.resume-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.resume-dialog {
  background: var(--card-color);
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.resume-dialog-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.resume-dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-color);
}

.resume-dialog-content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.resume-info {
  margin: 0 0 20px 0;
  color: var(--text-color-2);
  font-size: 14px;
}

.job-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.job-item {
  padding: 16px;
  background: var(--base-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.job-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.job-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.job-name {
  font-weight: 600;
  color: var(--text-color);
  font-size: 14px;
}

.job-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.status-active {
  background: #e3f2fd;
  color: #1976d2;
}

.status-paused {
  background: #fff3e0;
  color: #f57c00;
}

.status-completed {
  background: #e8f5e9;
  color: #388e3c;
}

.status-failed {
  background: #ffebee;
  color: #d32f2f;
}

.job-progress {
  font-size: 13px;
  color: var(--text-color-2);
  display: flex;
  gap: 12px;
}

.failed-count {
  color: #d32f2f;
}

.job-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: var(--text-color-3);
}

.job-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.resume-button,
.cancel-button,
.remove-button {
  padding: 6px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.resume-button {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.resume-button:hover:not(:disabled) {
  opacity: 0.9;
}

.resume-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cancel-button {
  background: var(--card-color);
  color: var(--text-color);
}

.cancel-button:hover {
  border-color: #d32f2f;
  color: #d32f2f;
}

.remove-button {
  background: var(--card-color);
  color: var(--text-color-2);
}

.remove-button:hover {
  border-color: var(--border-color);
  color: var(--text-color);
}

.resume-dialog-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
}

.dismiss-button {
  padding: 8px 16px;
  background: var(--card-color);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-color);
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.dismiss-button:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}
</style>
